export const extra = {
  da: {
    nav: { platform: "Platform", architecture: "Arkitektur", benchmark: "Benchmark", security: "Sikkerhed", team: "Team" },
    bookDemo: "Book en demo",
    hero: {
      eyebrow: "Privat industriel AI til enterprise-fremstilling",
      line1: "Stil en opgave.",
      line2: "Agenter gør arbejdet.",
      line3: "Hver indsigt sporbar.",
      sub: "Streamline360 er den agentiske AI-platform bygget til enterprise-fremstilling. Spørg på almindeligt driftssprog — specialiserede agenter henter live anlægskontekst fra din Knowledge Graph og svarer på sekunder. On-premise og på edge: dine data forlader aldrig fabrikken.",
      ctaBenchmark: "Se benchmarket",
      stats: [
        { value: "90,2 %", label: "Benchmark-nøjagtighed" },
        { value: "~0,05 $", label: "Pris pr. opgave" },
        { value: "Sekunder", label: "Tid til indsigt" },
        { value: "100 %", label: "Sporbar til kilden" },
      ],
    },
    marquee: ["NUL HALLUCINATIONER", "100 % SPORBART", "KNOWLEDGE-GRAPH-FUNDERET", "ON-PREMISE & EDGE", "FORESPØRGSLER PÅ DAGLIGDANSK", "OPC-UA · MQTT · HISTORIAN-APIS"],
    problem: {
      tag: "Problemet",
      h2: "Din fabrik kan stadig ikke bruge sprogmodellernes kraft i dag.",
      sub: "Global fremstilling taber milliarder til uplanlagte stop — alligevel holdes generisk AI ude af fabrikshallen af tre strukturelle årsager.",
      cards: [
        { title: "Datasuverænitetsmuren", body: "Sikkerheds- og kritisk-infrastrukturregler forbyder strengt at sende proprietær telemetri og procesopskrifter uden for fabrikkens perimeter. Cloud-AI er udelukket." },
        { title: "Legacy-datasiloer", body: "Afgørende fabriksviden forbliver fanget på tværs af usammenhængende historians, ældre SCADA-systemer, kontrolnetværk og fysiske vagtlogs." },
        { title: "Pilotfælden", body: "Generiske frontier-modeller er for langsomme, for dyre til løbende forespørgsler og tilbøjelige til hallucinationer under kritiske produktionshændelser." },
      ],
    },
    stepper: {
      tag: "Fra opgave til resultat",
      h2: "Du stiller en opgave. Agenter klarer resten.",
      steps: [
        { n: "01", label: "Stil en opgave", quote: "»Hvorfor standsede linje 3 i går?«", body: "Fabrikspersonale spørger på almindeligt driftssprog. Ingen forespørgsler at skrive, ingen dashboards at konfigurere, ingen data at eksportere." },
        { n: "02", label: "Agenter henter kontekst", body: "Specialiserede agenter henter anlægsdata og relationer fra Knowledge Graph — kun hvad opgaven kræver, intet mere." },
        { n: "03", label: "Agenter analyserer", body: "Stop, alarmer, parametre og trends analyseres mod verificeret anlægskontekst — aldrig gætterier på rå tekst." },
        { n: "04", label: "Agenter producerer output", body: "Et dashboard bygges, en rodårsag identificeres, anbefalinger formuleres — hver med dokumentation." },
        { n: "05", label: "Du får resultatet", body: "Indsigter, anbefalinger og rodårsager leveret på sekunder — hver eneste sporbar til sin datakilde, fra ende til anden." },
      ],
    },
    arch: {
      tag: "Arkitekturen",
      h2: "Én Knowledge Graph. En datablind grænse.",
      sub: "Den lokale motor forbinder live maskintelemetri med fabrikkens driftsviden inden for en sikker perimeter. Rådata bliver inde — kun abstraheret kontekst bevæger sig.",
      tour: [
        { label: "Trin 1 — Dine data strømmer ind", text: "Syv strømme af fabriksintelligens — historians, kontrolsystemer, MES, vedligehold, laboratorium, dokumenter, ERP — strømmer ind som rå telemetri." },
        { label: "Trin 2 — Den datablinde grænse", text: "Ved perimetren abstraheres rå payloads til operationelle metadata. Intet råt krydser nogensinde." },
        { label: "Trin 3 — Knowledge Graph", text: "Maskintelemetri flettes sammen med menneskelig driftsviden — SOP'er, vagtlogs, fabriksregler — beregnet fuldstændigt on-premise." },
        { label: "Trin 4 — Specialiserede agenter", text: "Agenter fortolker din opgave på driftssprog og henter kun den kontekst, de har brug for." },
        { label: "Trin 5 — Betroede output", text: "Dashboards, rodårsager, alarmindsigter og anbefalinger strømmer tilbage — hver enkelt sporbar til kilden." },
        { label: "Trin 6 — Cloud forbliver ude", text: "Stien til frontier cloud-AI er blokeret af arkitekturen, ikke af politikker. Din telemetri forlader aldrig fabrikken." },
      ],
      legend: ["Rå telemetri — bliver på fabrikken", "Abstraherede metadata — agenter reasoner over dette", "Cloud-eksfiltrering — blokeret"],
      defaultDesc: "Hold musen over en datakilde, grænsen eller Knowledge Graph for at spore, hvordan data flyder — og hvad der aldrig forlader fabrikken.",
      replay: "Afspil turen igen",
      connectors: "Konnektorer",
      yourData: "Dine data",
      runsLocally: "KØRER LOKALT",
      blocked: "BLOKERET",
      kgMobileTitle: "Knowledge Graph",
      kgMobileBody: "Dual-stream-kontekst bag en datablind grænse: live telemetri flettet med SOP'er, vagtlogs og fabriksregler. Rådata forlader aldrig fabrikken.",
      agentsTitle: "Specialiserede agenter",
      outputsTitle: "Betroede output",
      descs: {
        hub: "Dual-stream-kontekst: live maskintelemetri flettet med fabrikkens driftsviden — beregnet fuldstændigt on-premise.",
        boundary: "Rå payloads abstraheres til operationelle metadata ved perimetren. Modeller kører lokalt — intet ekfiltreres til cloud.",
        agents: "Specialiserede agenter fortolker operationel hensigt, henter kun relevant kontekst og samler svar med fuld kildesporing.",
        cloud: "Generisk cloud-AI ville kræve streaming af rå telemetri off-site. Grænsen blokerer det — per arkitektur, ikke politik.",
      },
      sourceDescs: [
        "Tidsserie-procestrends strømmer ind som rå telemetri — kun abstraherede operationelle metadata krydser grænsen.",
        "Live styresignaler og alarmer bliver på dit kontrolnetværk. Agenter reasoner over kontekst, aldrig rå payloads.",
        "Produktionsordrer, OEE-hændelser og shopfloor-data strømmer ind fra MES-laget.",
        "Arbejdsordrer og vedligeholdshistorik forankrer hver rodårsagsanalyse i, hvad der rent faktisk blev gjort på gulvet.",
        "Laboratorieresultater smelter sammen med batchkontekst, så kvalitetsafvigelser korrelerer direkte med procesforhold.",
        "Driftsmanualer og fabriksregler begrænser hver anbefaling — intet overskrider dine forvaliderede parametre.",
        "Ordrer, tidsplaner og lager forbinder produktionskontekst med forretningsprioriteter.",
      ],
      outputDescs: [
        "Live driftsdashboards, bygget på forespørgsel fra anlægsdata.",
        "Afvigelser og stop sporet til deres konkrete årsag, med dokumentation.",
        "Hvad en alarm betyder, hvorfor den udløste, og hvad der skal gøres derefter.",
        "Handlingsbare næste skridt, bundet til validerede driftsparametre.",
      ],
    },
    deliver: {
      tag: "Hvad du får",
      h2: "Svar, ikke rå tekst.",
      liveExample: "Live-eksempel — »Byg et OEE-dashboard til presseafdelingen.«",
      cards: [
        { title: "Dashboards", body: "Bygget på forespørgsel, fodret fra live anlægsdata — OEE, stop, kvalitet, energi." },
        { title: "Rodårsagsanalyse", body: "Den konkrete årsag bag en afvigelse eller et stop — identificeret og dokumenteret." },
        { title: "Alarmindsigter", body: "Hvad en alarm betyder, hvorfor den udløste, og hvad der skal gøres." },
        { title: "Anbefalinger", body: "Handlingsbare næste skridt — hver bundet til forvaliderede driftsparametre." },
      ],
    },
    bench: {
      tag: "Benchmarket",
      h2: "Overgår frontier-AI. På dine data.",
      sub: "Samme opgaver, samme fabrikdatasæt, side om side — bedømt af uafhængige procesingeniører. Fokuseret hentning slår rå-tekst-behandling på alle metrikker.",
      stats: [
        { label: "Nøjagtighed", sub: "vs 15,8–17,7 % for frontier-modeller" },
        { label: "Pris pr. opgave", sub: "vs op til ~1,80 $ pr. opgave" },
        { label: "Tokens pr. opgave", sub: "vs 500k+ for frontier-modeller" },
      ],
      theirs: "Frontier-AI-modeller",
      bars: ["Nøjagtighed", "Pris pr. opgave", "Tokens pr. opgave"],
      footnote: "Benchmark, august 2026 · identiske fabrikdatasæt · menneskeverificeret scoring. Lavere pris- og token-bjælker er bedre.",
    },
    security: {
      tag: "Sikkerhed & deployment",
      h2: "Din telemetri forlader aldrig fabrikken.",
      sub: "Streamline360 er privacy-first per arkitektur, ikke politik. Den datablinde grænse holder proprietære opskrifter og procesdata inde i din sikre perimeter.",
      cards: [
        { title: "On-Premise & Edge", body: "Installeres direkte på edge eller i dit fabriksnetværk. Ingen cloud-afhængighed, ingen latensbarriere." },
        { title: "Lokal datagrænse", body: "Rå telemetri abstraheres til operationelle metadata. Modeller kører lokalt — intet ekfiltreres." },
        { title: "Nul hallucinationer", body: "Anbefalinger er strengt bundet til dine driftsmanualer og forvaliderede parametre." },
        { title: "Fuld revisionssporbarhed", body: "Hver indsigt henviser til konkrete sensortags, tidsstempler og godkendte procedurer." },
      ],
      deployLabel: "Deployment",
      deployCaption: "Inden for din perimeter. Under din kontrol.",
      compliance: "Compliance-roadmap",
    },
    team: {
      tag: "Ledelse",
      h2: "Bygget af mennesker, der kender fabrikshallen.",
      sub: "Et grundlæggerteam på tværs af industriel AI, enterprise-software og produktionsdrift — besat af at gøre intelligens sikker for kritisk infrastruktur.",
      members: [
        { role: "Medstifter & CEO", bio: "Enterprise-software og industriel go-to-market. Leder strategi, partnerskaber og OEM-kanalnetværket." },
        { role: "Medstifter & CTO", bio: "Industriel AI og edge-systemer. Arkitekt bag Knowledge Graph-motoren og den datablinde grænse." },
        { role: "Head of Product", bio: "Produktionsdrift og HMI-design. Ejer agent-oplevelsen på dagligdansk på fabriksgulvet." },
      ],
      onRequest: "Profil på forespørgsel",
    },
    contact: {
      tag: "Kom i gang",
      h2: "Se det på dine data.",
      sub: "Book en teknisk demo — vi kører Streamline360 mod en stikprøve af dine anlægsdata, on-premise, inden for din perimeter.",
      bullets: ["60-dages teknisk valideringsforløb", "Deployes på edge eller on-premise", "Forbinder til historians, SCADA og CMMS out of the box"],
      name: "Fulde navn",
      company: "Virksomhed",
      email: "Arbejdsmail",
      message: "Hvad skal Streamline360 analysere? (valgfrit)",
      submit: "Anmod om en demo",
      toastOk: "Anmodning modtaget. Vores team vender tilbage snarest.",
      toastErr: "Noget gik galt. Prøv igen.",
      note: "Dine oplysninger bliver hos os. Ingen data forlader din fabrik under en demo.",
    },
    footer: {
      tagline: "Privat, on-premise agentisk AI til enterprise-fremstilling.",
      impressum: "Impressum",
      madeIn: "Privat, on-premise agentisk AI til enterprise-fremstilling — lavet i København.",
      rights: "© 2026 Streamline360 ApS. Alle rettigheder forbeholdes.",
      neverLeaves: "Dine data forlader aldrig fabrikken.",
      privacy: "Privatlivspolitik",
      terms: "Vilkår for brug",
      contact: "Kontakt",
    },
  },
  fr: {
    nav: { platform: "Plateforme", architecture: "Architecture", benchmark: "Benchmark", security: "Sécurité", team: "Équipe" },
    bookDemo: "Réserver une démo",
    hero: {
      eyebrow: "IA industrielle privée pour la fabrication d'entreprise",
      line1: "Assignez une tâche.",
      line2: "Les agents font le travail.",
      line3: "Chaque insight tracé.",
      sub: "Streamline360 est la plateforme d'IA agentique conçue pour la fabrication d'entreprise. Posez vos questions en langage opérationnel — des agents spécialisés extraient le contexte usine en direct de votre Knowledge Graph et répondent en quelques secondes. On-premise et à l'edge : vos données ne quittent jamais l'usine.",
      ctaBenchmark: "Voir le benchmark",
      stats: [
        { value: "90,2 %", label: "Précision du benchmark" },
        { value: "~0,05 $", label: "Coût par tâche" },
        { value: "Secondes", label: "Délai d'obtention" },
        { value: "100 %", label: "Traçable à la source" },
      ],
    },
    marquee: ["ZÉRO HALLUCINATION", "100 % TRAÇABLE", "FONDÉ SUR UN KNOWLEDGE GRAPH", "ON-PREMISE & EDGE", "REQUÊTES EN LANGAGE NATUREL", "OPC-UA · MQTT · APIS HISTORIAN"],
    problem: {
      tag: "Le problème",
      h2: "Votre usine ne peut toujours pas exploiter la puissance des modèles de langage aujourd'hui.",
      sub: "L'industrie mondiale perd des milliards à cause des arrêts non planifiés — pourtant l'IA générique reste exclue de l'atelier pour trois raisons structurelles.",
      cards: [
        { title: "Le mur de la souveraineté des données", body: "Les règles de sécurité et d'infrastructure critique interdisent strictement d'envoyer télémétrie propriétaire et recettes de process hors du périmètre de l'usine. L'IA cloud est hors de question." },
        { title: "Silos de données legacy", body: "L'intelligence d'usine cruciale reste piégée dans des historians disparates, des systèmes SCADA legacy, des réseaux de contrôle et des journaux d'équipe papier." },
        { title: "Le piège du pilote", body: "Les modèles frontier génériques sont trop lents, trop chers à interroger en continu et sujets aux hallucinations lors d'événements de production critiques." },
      ],
    },
    stepper: {
      tag: "De la tâche au résultat",
      h2: "Vous assignez une tâche. Les agents s'occupent du reste.",
      steps: [
        { n: "01", label: "Assignez une tâche", quote: "« Pourquoi la ligne 3 s'est-elle arrêtée hier ? »", body: "Le personnel demande en langage opérationnel standard. Aucune requête à écrire, aucun dashboard à configurer, aucune donnée à exporter." },
        { n: "02", label: "Les agents extraient le contexte", body: "Des agents spécialisés récupèrent données et relations de l'usine depuis le Knowledge Graph — uniquement ce dont la tâche a besoin." },
        { n: "03", label: "Les agents analysent", body: "Arrêts, alarmes, paramètres et tendances sont analysés contre un contexte usine vérifié — jamais de conjecture sur du texte brut." },
        { n: "04", label: "Les agents produisent le résultat", body: "Un dashboard est construit, une cause racine identifiée, des recommandations formulées — chacune étayée par des preuves." },
        { n: "05", label: "Vous recevez le résultat", body: "Insights, recommandations et causes racines livrés en quelques secondes — chacun traçable à sa source, de bout en bout." },
      ],
    },
    arch: {
      tag: "L'architecture",
      h2: "Un Knowledge Graph. Une frontière data-blind.",
      sub: "Le moteur local relie la télémétrie machine en direct au savoir opérationnel de votre usine dans un périmètre sécurisé. Les données brutes restent à l'intérieur — seul le contexte abstrait circule.",
      tour: [
        { label: "Étape 1 — Vos données affluent", text: "Sept flux d'intelligence d'usine — historians, systèmes de contrôle, MES, maintenance, labo, documents, ERP — affluent en télémétrie brute." },
        { label: "Étape 2 — La frontière data-blind", text: "Au périmètre, les payloads brutes sont abstraites en métadonnées opérationnelles. Rien de brut ne traverse jamais." },
        { label: "Étape 3 — Le Knowledge Graph", text: "La télémétrie machine fusionne avec le savoir humain — SOP, journaux d'équipe, règles d'usine — calculé entièrement on-premise." },
        { label: "Étape 4 — Agents spécialisés", text: "Les agents interprètent votre tâche en langage opérationnel et ne récupèrent que le contexte nécessaire." },
        { label: "Étape 5 — Résultats fiables", text: "Dashboards, causes racines, insights d'alarmes et recommandations reviennent — chacun traçable à la source." },
        { label: "Étape 6 — Le cloud reste dehors", text: "Le chemin vers l'IA cloud frontier est bloqué par l'architecture, pas par une politique. Votre télémétrie ne quitte jamais l'usine." },
      ],
      legend: ["Télémétrie brute — reste dans l'usine", "Métadonnées abstraites — base de raisonnement des agents", "Exfiltration cloud — bloquée"],
      defaultDesc: "Survolez une source de données, la frontière ou le Knowledge Graph pour suivre le flux de données — et ce qui ne part jamais.",
      replay: "Rejouer la visite",
      connectors: "Connecteurs",
      yourData: "Vos données",
      runsLocally: "TOURNE EN LOCAL",
      blocked: "BLOQUÉ",
      kgMobileTitle: "Knowledge Graph",
      kgMobileBody: "Contexte dual-stream derrière une frontière data-blind : télémétrie live fusionnée avec SOP, journaux d'équipe et règles d'usine. Les données brutes ne partent jamais.",
      agentsTitle: "Agents spécialisés",
      outputsTitle: "Résultats fiables",
      descs: {
        hub: "Contexte dual-stream : télémétrie machine live fusionnée avec le savoir opérationnel de votre usine — calculé entièrement on-premise.",
        boundary: "Les payloads brutes sont abstraites en métadonnées opérationnelles au périmètre. Les modèles tournent en local — rien n'est exfiltré vers le cloud.",
        agents: "Des agents spécialisés interprètent l'intention opérationnelle, récupèrent le contexte pertinent et assemblent des réponses avec une trace complète des sources.",
        cloud: "L'IA cloud générique exigerait de streamer la télémétrie brute hors site. La frontière le bloque — par architecture, pas par politique.",
      },
      sourceDescs: [
        "Les tendances de process en séries temporelles affluent en télémétrie brute — seules les métadonnées opérationnelles abstraites traversent la frontière.",
        "Signaux de contrôle et alarmes live restent sur votre réseau de contrôle. Les agents raisonnent sur le contexte, jamais sur les payloads brutes.",
        "Ordres de fabrication, événements OEE et données d'atelier affluent depuis la couche MES.",
        "Ordres de travail et historique de maintenance ancrent chaque analyse de cause racine dans ce qui a réellement été fait en atelier.",
        "Les résultats de labo fusionnent avec le contexte batch pour corréler directement écarts qualité et conditions de process.",
        "Manuels d'exploitation et règles d'usine encadrent chaque recommandation — rien ne dépasse vos paramètres pré-validés.",
        "Commandes, plannings et stocks relient le contexte de production aux priorités métier.",
      ],
      outputDescs: [
        "Dashboards opérationnels live, construits à la demande à partir des données d'usine.",
        "Écarts et arrêts tracés jusqu'à leur cause précise, avec preuves.",
        "Ce qu'une alarme signifie, pourquoi elle a sauté, et quoi faire ensuite.",
        "Prochaines étapes actionnables, liées à des paramètres d'exploitation validés.",
      ],
    },
    deliver: {
      tag: "Ce que vous obtenez",
      h2: "Des réponses, pas du texte brut.",
      liveExample: "Exemple en direct — « Construis un dashboard OEE pour l'atelier presses. »",
      cards: [
        { title: "Dashboards", body: "Construits à la demande, alimentés par les données d'usine live — OEE, arrêts, qualité, énergie." },
        { title: "Analyse de cause racine", body: "La raison précise derrière un écart ou un arrêt — identifiée et documentée." },
        { title: "Insights d'alarmes", body: "Ce qu'une alarme signifie, pourquoi elle a sauté, et quoi faire." },
        { title: "Recommandations", body: "Prochaines étapes actionnables — chacune liée à des paramètres d'exploitation pré-validés." },
      ],
    },
    bench: {
      tag: "Le benchmark",
      h2: "Surpasse l'IA frontier. Sur vos données.",
      sub: "Mêmes tâches, mêmes jeux de données d'usine, côte à côte — notés par des ingénieurs procédés indépendants. La récupération ciblée bat le traitement de texte brut sur chaque métrique.",
      stats: [
        { label: "Précision", sub: "vs 15,8–17,7 % pour les modèles frontier" },
        { label: "Coût par tâche", sub: "vs jusqu'à ~1,80 $ par tâche" },
        { label: "Tokens par tâche", sub: "vs 500k+ pour les modèles frontier" },
      ],
      theirs: "Modèles d'IA frontier",
      bars: ["Précision", "Coût par tâche", "Tokens par tâche"],
      footnote: "Benchmark, août 2026 · jeux de données d'usine identiques · scoring vérifié par des humains. Des barres de coût et de tokens plus basses sont meilleures.",
    },
    security: {
      tag: "Sécurité & déploiement",
      h2: "Votre télémétrie ne quitte jamais l'usine.",
      sub: "Streamline360 est privacy-first par architecture, pas par politique. La frontière data-blind garde recettes propriétaires et données de process dans votre périmètre sécurisé.",
      cards: [
        { title: "On-Premise & Edge", body: "S'installe directement à l'edge ou dans votre réseau d'usine. Aucune dépendance cloud, aucune barrière de latence." },
        { title: "Frontière de données locale", body: "La télémétrie brute est abstraite en métadonnées opérationnelles. Les modèles tournent en local — rien n'est exfiltré." },
        { title: "Zéro hallucination", body: "Les recommandations sont strictement liées à vos manuels d'exploitation et paramètres pré-validés." },
        { title: "Auditabilité complète", body: "Chaque insight renvoie à des tags capteurs précis, des horodatages et des procédures approuvées." },
      ],
      deployLabel: "Déploiement",
      deployCaption: "Dans votre périmètre. Sous votre contrôle.",
      compliance: "Feuille de route conformité",
    },
    team: {
      tag: "Direction",
      h2: "Construit par des gens qui connaissent l'atelier.",
      sub: "Une équipe fondatrice couvrant IA industrielle, logiciels d'entreprise et opérations de fabrication — obsédée par rendre l'intelligence sûre pour les infrastructures critiques.",
      members: [
        { role: "Co-fondateur & CEO", bio: "Logiciels d'entreprise et go-to-market industriel. Dirige la stratégie, les partenariats et le réseau de canaux OEM." },
        { role: "Co-fondateur & CTO", bio: "IA industrielle et systèmes edge. Architecte du moteur Knowledge Graph et de la frontière data-blind." },
        { role: "Head of Product", bio: "Opérations de fabrication et design HMI. Responsable de l'expérience agent en langage naturel en atelier." },
      ],
      onRequest: "Profil sur demande",
    },
    contact: {
      tag: "Démarrer",
      h2: "Voyez-le sur vos données.",
      sub: "Réservez une démo technique — nous exécuterons Streamline360 sur un échantillon de vos données d'usine, on-premise, dans votre périmètre.",
      bullets: ["Parcours de validation technique de 60 jours", "Se déploie à l'edge ou on-premise", "Se connecte aux historians, SCADA, CMMS nativement"],
      name: "Nom complet",
      company: "Entreprise",
      email: "E-mail professionnel",
      message: "Que souhaitez-vous que Streamline360 analyse ? (optionnel)",
      submit: "Demander une démo",
      toastOk: "Demande reçue. Notre équipe vous contactera rapidement.",
      toastErr: "Une erreur est survenue. Veuillez réessayer.",
      note: "Vos informations restent chez nous. Aucune donnée ne quitte votre usine pendant une démo.",
    },
    footer: {
      tagline: "IA agentique privée, on-premise, pour la fabrication d'entreprise.",
      impressum: "Mentions légales",
      madeIn: "IA industrielle privée pour la fabrication d'entreprise — conçue à Copenhague.",
      rights: "© 2026 Streamline360 ApS. Tous droits réservés.",
      neverLeaves: "Vos données ne quittent jamais l'usine.",
      privacy: "Politique de confidentialité",
      terms: "Conditions d'utilisation",
      contact: "Contact",
    },
  },
  es: {
    nav: { platform: "Plataforma", architecture: "Arquitectura", benchmark: "Benchmark", security: "Seguridad", team: "Equipo" },
    bookDemo: "Reservar una demo",
    hero: {
      eyebrow: "IA industrial privada para manufactura empresarial",
      line1: "Asigna una tarea.",
      line2: "Los agentes hacen el trabajo.",
      line3: "Cada insight trazado.",
      sub: "Streamline360 es la plataforma de IA agéntica diseñada para la manufactura empresarial. Pregunta en lenguaje operativo cotidiano — agentes especializados extraen el contexto de planta en vivo de tu Knowledge Graph y responden en segundos. On-premise y en el edge: tus datos nunca salen de la planta.",
      ctaBenchmark: "Ver el benchmark",
      stats: [
        { value: "90,2 %", label: "Precisión del benchmark" },
        { value: "~0,05 $", label: "Coste por tarea" },
        { value: "Segundos", label: "Tiempo hasta el insight" },
        { value: "100 %", label: "Trazable a la fuente" },
      ],
    },
    marquee: ["CERO ALUCINACIONES", "100 % TRAZABLE", "BASADO EN KNOWLEDGE GRAPH", "ON-PREMISE Y EDGE", "CONSULTAS EN LENGUAJE NATURAL", "OPC-UA · MQTT · APIS DE HISTORIAN"],
    problem: {
      tag: "El problema",
      h2: "Tu planta aún no puede usar el poder de los modelos de lenguaje.",
      sub: "La manufactura global pierde miles de millones por paradas no planificadas — sin embargo, la IA genérica sigue fuera de la planta por tres razones estructurales.",
      cards: [
        { title: "El muro de soberanía de datos", body: "Las normas de seguridad e infraestructura crítica prohíben estrictamente enviar telemetría propietaria y recetas de proceso fuera del perímetro de la planta. La IA en la nube no es una opción." },
        { title: "Silos de datos heredados", body: "La inteligencia crítica de fábrica queda atrapada en historians dispares, sistemas SCADA heredados, redes de control y registros de turno en papel." },
        { title: "La trampa del piloto", body: "Los modelos frontier genéricos son demasiado lentos, demasiado caros para consultas continuas y propensos a alucinaciones durante eventos críticos de producción." },
      ],
    },
    stepper: {
      tag: "De la tarea al resultado",
      h2: "Tú asignas una tarea. Los agentes se encargan del resto.",
      steps: [
        { n: "01", label: "Asigna una tarea", quote: "«¿Por qué se paró ayer la Línea 3?»", body: "El personal pregunta en lenguaje operativo estándar. Sin consultas que escribir, sin dashboards que configurar, sin datos que exportar." },
        { n: "02", label: "Los agentes extraen contexto", body: "Agentes especializados recuperan datos y relaciones de planta del Knowledge Graph — solo lo que la tarea necesita." },
        { n: "03", label: "Los agentes analizan", body: "Paradas, alarmas, parámetros y tendencias se analizan contra contexto de planta verificado — nunca conjeturas sobre texto en bruto." },
        { n: "04", label: "Los agentes producen el resultado", body: "Se construye un dashboard, se identifica una causa raíz, se formulan recomendaciones — cada una con evidencia." },
        { n: "05", label: "Recibes el resultado", body: "Insights, recomendaciones y causas raíz en segundos — cada uno trazable a su fuente, de extremo a extremo." },
      ],
    },
    arch: {
      tag: "La arquitectura",
      h2: "Un Knowledge Graph. Una frontera ciega a los datos.",
      sub: "El motor local conecta la telemetría de máquinas en vivo con el conocimiento operativo de tu planta dentro de un perímetro seguro. Los datos en bruto se quedan dentro — solo se mueve el contexto abstraído.",
      tour: [
        { label: "Paso 1 — Tus datos fluyen", text: "Siete flujos de inteligencia de planta — historians, sistemas de control, MES, mantenimiento, laboratorio, documentos, ERP — entran como telemetría en bruto." },
        { label: "Paso 2 — La frontera ciega a los datos", text: "En el perímetro, los payloads en bruto se abstraen en metadatos operativos. Nada en bruto cruza jamás." },
        { label: "Paso 3 — El Knowledge Graph", text: "La telemetría de máquinas se fusiona con el conocimiento humano — SOP, registros de turno, reglas de planta — calculado totalmente on-premise." },
        { label: "Paso 4 — Agentes especializados", text: "Los agentes interpretan tu tarea en lenguaje operativo y recuperan solo el contexto que necesitan." },
        { label: "Paso 5 — Resultados confiables", text: "Dashboards, causas raíz, insights de alarmas y recomendaciones regresan — cada uno trazable a la fuente." },
        { label: "Paso 6 — La nube se queda fuera", text: "El camino hacia la IA frontier en la nube está bloqueado por arquitectura, no por política. Tu telemetría nunca sale de la planta." },
      ],
      legend: ["Telemetría en bruto — se queda en la planta", "Metadatos abstraídos — base de razonamiento de los agentes", "Exfiltración a la nube — bloqueada"],
      defaultDesc: "Pasa el cursor sobre una fuente de datos, la frontera o el Knowledge Graph para seguir el flujo de datos — y lo que nunca sale.",
      replay: "Repetir el recorrido",
      connectors: "Conectores",
      yourData: "Tus datos",
      runsLocally: "SE EJECUTA EN LOCAL",
      blocked: "BLOQUEADO",
      kgMobileTitle: "Knowledge Graph",
      kgMobileBody: "Contexto dual-stream tras una frontera ciega a los datos: telemetría en vivo fusionada con SOP, registros de turno y reglas de planta. Los datos en bruto nunca salen.",
      agentsTitle: "Agentes especializados",
      outputsTitle: "Resultados confiables",
      descs: {
        hub: "Contexto dual-stream: telemetría de máquinas en vivo fusionada con el conocimiento operativo de tu planta — calculado totalmente on-premise.",
        boundary: "Los payloads en bruto se abstraen en metadatos operativos en el perímetro. Los modelos se ejecutan en local — nada se exfiltra a la nube.",
        agents: "Agentes especializados interpretan la intención operativa, recuperan solo el contexto relevante y ensamblan respuestas con traza completa de fuentes.",
        cloud: "La IA genérica en la nube requeriría transmitir telemetría en bruto fuera del sitio. La frontera lo bloquea — por arquitectura, no por política.",
      },
      sourceDescs: [
        "Las tendencias de proceso en series temporales entran como telemetría en bruto — solo los metadatos operativos abstraídos cruzan la frontera.",
        "Señales de control y alarmas en vivo permanecen en tu red de control. Los agentes razonan sobre contexto, nunca sobre payloads en bruto.",
        "Órdenes de fabricación, eventos OEE y datos de ejecución de planta entran desde la capa MES.",
        "Órdenes de trabajo e historial de mantenimiento anclan cada análisis de causa raíz en lo que realmente se hizo en planta.",
        "Los resultados de laboratorio se fusionan con el contexto de lote para correlacionar desviaciones de calidad con condiciones de proceso.",
        "Manuales de operación y reglas de planta limitan cada recomendación — nada supera tus parámetros prevalidados.",
        "Pedidos, planificaciones e inventario conectan el contexto de producción con las prioridades del negocio.",
      ],
      outputDescs: [
        "Dashboards operativos en vivo, construidos bajo demanda a partir de datos de planta.",
        "Desviaciones y paradas trazadas hasta su causa concreta, con evidencia.",
        "Qué significa una alarma, por qué saltó y qué hacer después.",
        "Próximos pasos accionables, vinculados a parámetros operativos validados.",
      ],
    },
    deliver: {
      tag: "Lo que obtienes",
      h2: "Respuestas, no texto en bruto.",
      liveExample: "Ejemplo en vivo — «Construye un dashboard OEE para el taller de prensas.»",
      cards: [
        { title: "Dashboards", body: "Construidos bajo demanda, alimentados por datos de planta en vivo — OEE, paradas, calidad, energía." },
        { title: "Análisis de causa raíz", body: "La razón concreta detrás de una desviación o parada — identificada y documentada." },
        { title: "Insights de alarmas", body: "Qué significa una alarma, por qué saltó y qué hacer al respecto." },
        { title: "Recomendaciones", body: "Próximos pasos accionables — cada uno vinculado a parámetros operativos prevalidados." },
      ],
    },
    bench: {
      tag: "El benchmark",
      h2: "Supera a la IA frontier. Con tus datos.",
      sub: "Mismas tareas, mismos conjuntos de datos de fábrica, lado a lado — puntuados por ingenieros de procesos independientes. La recuperación enfocada supera al procesamiento de texto en bruto en cada métrica.",
      stats: [
        { label: "Precisión", sub: "vs 15,8–17,7 % en modelos frontier" },
        { label: "Coste por tarea", sub: "vs hasta ~1,80 $ por tarea" },
        { label: "Tokens por tarea", sub: "vs 500k+ en modelos frontier" },
      ],
      theirs: "Modelos de IA frontier",
      bars: ["Precisión", "Coste por tarea", "Tokens por tarea"],
      footnote: "Benchmark, agosto 2026 · conjuntos de datos de fábrica idénticos · puntuación verificada por humanos. Barras de coste y tokens más bajas son mejores.",
    },
    security: {
      tag: "Seguridad y despliegue",
      h2: "Tu telemetría nunca sale de la planta.",
      sub: "Streamline360 es privacy-first por arquitectura, no por política. La frontera ciega a los datos mantiene recetas propietarias y datos de proceso dentro de tu perímetro seguro.",
      cards: [
        { title: "On-Premise y Edge", body: "Se instala directamente en el edge o dentro de tu red de planta. Sin dependencia de la nube, sin barrera de latencia." },
        { title: "Frontera de datos local", body: "La telemetría en bruto se abstrae en metadatos operativos. Los modelos se ejecutan en local — nada se exfiltra." },
        { title: "Cero alucinaciones", body: "Las recomendaciones están estrictamente vinculadas a tus manuales de operación y parámetros prevalidados." },
        { title: "Auditabilidad completa", body: "Cada insight enlaza a tags de sensores específicos, marcas de tiempo y procedimientos aprobados." },
      ],
      deployLabel: "Despliegue",
      deployCaption: "Dentro de tu perímetro. Bajo tu control.",
      compliance: "Hoja de ruta de cumplimiento",
    },
    team: {
      tag: "Liderazgo",
      h2: "Construido por personas que conocen la planta.",
      sub: "Un equipo fundador que abarca IA industrial, software empresarial y operaciones de manufactura — obsesionado con hacer la inteligencia segura para infraestructura crítica.",
      members: [
        { role: "Cofundador & CEO", bio: "Software empresarial y go-to-market industrial. Lidera estrategia, alianzas y la red de canales OEM." },
        { role: "Cofundador & CTO", bio: "IA industrial y sistemas edge. Arquitecto del motor Knowledge Graph y de la frontera ciega a los datos." },
        { role: "Head of Product", bio: "Operaciones de manufactura y diseño HMI. Responsable de la experiencia de agentes en lenguaje natural en planta." },
      ],
      onRequest: "Perfil bajo petición",
    },
    contact: {
      tag: "Empezar",
      h2: "Míralo con tus datos.",
      sub: "Reserva una demo técnica — ejecutaremos Streamline360 contra una muestra de tus datos de planta, on-premise, dentro de tu perímetro.",
      bullets: ["Ruta de validación técnica de 60 días", "Se despliega en el edge u on-premise", "Conecta con historians, SCADA y CMMS de serie"],
      name: "Nombre completo",
      company: "Empresa",
      email: "Correo de trabajo",
      message: "¿Qué te gustaría que Streamline360 analizara? (opcional)",
      submit: "Solicitar una demo",
      toastOk: "Solicitud recibida. Nuestro equipo te contactará en breve.",
      toastErr: "Algo salió mal. Inténtalo de nuevo.",
      note: "Tus datos se quedan con nosotros. Ningún dato sale de tu planta durante una demo.",
    },
    footer: {
      tagline: "IA agéntica privada, on-premise, para manufactura empresarial.",
      impressum: "Aviso legal",
      madeIn: "IA industrial privada para manufactura empresarial — hecha en Copenhague.",
      rights: "© 2026 Streamline360 ApS. Todos los derechos reservados.",
      neverLeaves: "Tus datos nunca salen de la planta.",
      privacy: "Política de privacidad",
      terms: "Términos de uso",
      contact: "Contacto",
    },
  },
  it: {
    nav: { platform: "Piattaforma", architecture: "Architettura", benchmark: "Benchmark", security: "Sicurezza", team: "Team" },
    bookDemo: "Prenota una demo",
    hero: {
      eyebrow: "AI industriale privata per la manifattura aziendale",
      line1: "Assegna un compito.",
      line2: "Gli agenti fanno il lavoro.",
      line3: "Ogni insight tracciato.",
      sub: "Streamline360 è la piattaforma di AI agentica pensata per la manifattura aziendale. Chiedi nel linguaggio operativo quotidiano — agenti specializzati estraggono il contesto live dell'impianto dal tuo Knowledge Graph e rispondono in pochi secondi. On-premise e all'edge: i tuoi dati non lasciano mai lo stabilimento.",
      ctaBenchmark: "Vedi il benchmark",
      stats: [
        { value: "90,2 %", label: "Accuratezza del benchmark" },
        { value: "~0,05 $", label: "Costo per attività" },
        { value: "Secondi", label: "Tempo all'insight" },
        { value: "100 %", label: "Tracciabile alla fonte" },
      ],
    },
    marquee: ["ZERO ALLUCINAZIONI", "100 % TRACCIABILE", "BASATO SU KNOWLEDGE GRAPH", "ON-PREMISE & EDGE", "QUERY IN LINGUAGGIO NATURALE", "OPC-UA · MQTT · API HISTORIAN"],
    problem: {
      tag: "Il problema",
      h2: "Il tuo stabilimento oggi non può ancora usare la potenza dei modelli linguistici.",
      sub: "La manifattura globale perde miliardi per fermi non pianificati — eppure l'AI generica resta fuori dalla fabbrica per tre ragioni strutturali.",
      cards: [
        { title: "Il muro della sovranità dei dati", body: "Le norme di sicurezza e di infrastruttura critica vietano rigorosamente di inviare telemetria proprietaria e ricette di processo fuori dal perimetro dello stabilimento. L'AI cloud non è un'opzione." },
        { title: "Silo di dati legacy", body: "L'intelligenza di fabbrica cruciale resta intrappolata tra historian disgiunti, sistemi SCADA legacy, reti di controllo e registri di turno cartacei." },
        { title: "La trappola del pilota", body: "I modelli frontier generici sono troppo lenti, troppo costosi per query continue e inclini ad allucinazioni durante eventi di produzione critici." },
      ],
    },
    stepper: {
      tag: "Dal compito al risultato",
      h2: "Tu assegni un compito. Gli agenti fanno il resto.",
      steps: [
        { n: "01", label: "Assegna un compito", quote: "«Perché la Linea 3 si è fermata ieri?»", body: "Il personale chiede nel linguaggio operativo standard. Nessuna query da scrivere, nessun dashboard da configurare, nessun dato da esportare." },
        { n: "02", label: "Gli agenti estraggono il contesto", body: "Agenti specializzati recuperano dati e relazioni dell'impianto dal Knowledge Graph — solo ciò che serve al compito." },
        { n: "03", label: "Gli agenti analizzano", body: "Fermi, allarmi, parametri e trend vengono analizzati contro il contesto verificato dell'impianto — mai congetture su testo grezzo." },
        { n: "04", label: "Gli agenti producono il risultato", body: "Un dashboard viene costruito, una causa radice identificata, raccomandazioni formulate — ognuna supportata da evidenze." },
        { n: "05", label: "Ricevi il risultato", body: "Insight, raccomandazioni e cause radice consegnati in pochi secondi — ognuno tracciabile alla sua fonte, end to end." },
      ],
    },
    arch: {
      tag: "L'architettura",
      h2: "Un Knowledge Graph. Un confine data-blind.",
      sub: "Il motore locale collega la telemetria live delle macchine al know-how operativo del tuo stabilimento dentro un perimetro sicuro. I dati grezzi restano dentro — si muove solo il contesto astratto.",
      tour: [
        { label: "Passo 1 — I tuoi dati fluiscono", text: "Sette flussi di intelligenza di impianto — historian, sistemi di controllo, MES, manutenzione, laboratorio, documenti, ERP — entrano come telemetria grezza." },
        { label: "Passo 2 — Il confine data-blind", text: "Al perimetro, i payload grezzi vengono astratti in metadati operativi. Nulla di grezzo attraversa mai." },
        { label: "Passo 3 — Il Knowledge Graph", text: "La telemetria delle macchine si fonde con il sapere umano — SOP, registri di turno, regole di impianto — calcolato interamente on-premise." },
        { label: "Passo 4 — Agenti specializzati", text: "Gli agenti interpretano il tuo compito in linguaggio operativo e recuperano solo il contesto necessario." },
        { label: "Passo 5 — Output affidabili", text: "Dashboard, cause radice, insight sugli allarmi e raccomandazioni ritornano — ognuno tracciabile alla fonte." },
        { label: "Passo 6 — Il cloud resta fuori", text: "Il percorso verso l'AI cloud frontier è bloccato dall'architettura, non da una policy. La tua telemetria non lascia mai lo stabilimento." },
      ],
      legend: ["Telemetria grezza — resta nello stabilimento", "Metadati astratti — base di ragionamento degli agenti", "Esfiltrazione cloud — bloccata"],
      defaultDesc: "Passa il mouse su una fonte dati, sul confine o sul Knowledge Graph per seguire il flusso dei dati — e ciò che non esce mai.",
      replay: "Rivedi il tour",
      connectors: "Connettori",
      yourData: "I tuoi dati",
      runsLocally: "GIRA IN LOCALE",
      blocked: "BLOCCATO",
      kgMobileTitle: "Knowledge Graph",
      kgMobileBody: "Contesto dual-stream dietro un confine data-blind: telemetria live fusa con SOP, registri di turno e regole di impianto. I dati grezzi non escono mai.",
      agentsTitle: "Agenti specializzati",
      outputsTitle: "Output affidabili",
      descs: {
        hub: "Contesto dual-stream: telemetria live delle macchine fusa con il know-how operativo dello stabilimento — calcolata interamente on-premise.",
        boundary: "I payload grezzi vengono astratti in metadati operativi al perimetro. I modelli girano in locale — nulla viene esfiltrato nel cloud.",
        agents: "Agenti specializzati interpretano l'intento operativo, recuperano solo il contesto rilevante e compongono risposte con traccia completa delle fonti.",
        cloud: "L'AI cloud generica richiederebbe lo streaming della telemetria grezza fuori sede. Il confine lo blocca — per architettura, non per policy.",
      },
      sourceDescs: [
        "I trend di processo in serie temporali entrano come telemetria grezza — solo i metadati operativi astratti attraversano il confine.",
        "Segnali di controllo e allarmi live restano nella tua rete di controllo. Gli agenti ragionano sul contesto, mai sui payload grezzi.",
        "Ordini di produzione, eventi OEE e dati di esecuzione di fabbrica arrivano dal livello MES.",
        "Ordini di lavoro e storico manutenzione ancorano ogni analisi di causa radice a ciò che è stato realmente fatto in fabbrica.",
        "I risultati di laboratorio si fondono con il contesto di lotto per correlare le deviazioni di qualità alle condizioni di processo.",
        "Manuali operativi e regole di impianto vincolano ogni raccomandazione — nulla supera i tuoi parametri pre-validati.",
        "Ordini, pianificazioni e inventario collegano il contesto produttivo alle priorità di business.",
      ],
      outputDescs: [
        "Dashboard operativi live, costruiti su richiesta dai dati di impianto.",
        "Deviazioni e fermi tracciati fino alla causa specifica, con evidenze.",
        "Cosa significa un allarme, perché è scattato e cosa fare dopo.",
        "Prossimi passi attuabili, vincolati a parametri operativi validati.",
      ],
    },
    deliver: {
      tag: "Cosa ottieni",
      h2: "Risposte, non testo grezzo.",
      liveExample: "Esempio live — «Costruisci una dashboard OEE per il reparto presse.»",
      cards: [
        { title: "Dashboard", body: "Costruite su richiesta, alimentate dai dati live dell'impianto — OEE, fermi, qualità, energia." },
        { title: "Analisi causa radice", body: "La ragione specifica dietro una deviazione o un fermo — identificata e documentata." },
        { title: "Insight sugli allarmi", body: "Cosa significa un allarme, perché è scattato e cosa fare." },
        { title: "Raccomandazioni", body: "Prossimi passi attuabili — ognuno vincolato a parametri operativi pre-validati." },
      ],
    },
    bench: {
      tag: "Il benchmark",
      h2: "Supera l'AI frontier. Sui tuoi dati.",
      sub: "Stessi compiti, stessi dataset di fabbrica, fianco a fianco — valutati da ingegneri di processo indipendenti. Il recupero mirato batte l'elaborazione di testo grezzo su ogni metrica.",
      stats: [
        { label: "Accuratezza", sub: "vs 15,8–17,7 % dei modelli frontier" },
        { label: "Costo per attività", sub: "vs fino a ~1,80 $ per attività" },
        { label: "Token per attività", sub: "vs 500k+ dei modelli frontier" },
      ],
      theirs: "Modelli di AI frontier",
      bars: ["Accuratezza", "Costo per attività", "Token per attività"],
      footnote: "Benchmark, agosto 2026 · dataset di fabbrica identici · punteggio verificato da umani. Barre di costo e token più basse sono migliori.",
    },
    security: {
      tag: "Sicurezza & deployment",
      h2: "La tua telemetria non lascia mai lo stabilimento.",
      sub: "Streamline360 è privacy-first per architettura, non per policy. Il confine data-blind mantiene ricette proprietarie e dati di processo dentro il tuo perimetro sicuro.",
      cards: [
        { title: "On-Premise & Edge", body: "Si installa direttamente all'edge o nella tua rete di stabilimento. Nessuna dipendenza dal cloud, nessuna barriera di latenza." },
        { title: "Confine dati locale", body: "La telemetria grezza viene astratta in metadati operativi. I modelli girano in locale — nulla viene esfiltrato." },
        { title: "Zero allucinazioni", body: "Le raccomandazioni sono strettamente vincolate ai tuoi manuali operativi e ai parametri pre-validati." },
        { title: "Auditabilità completa", body: "Ogni insight rimanda a tag di sensori specifici, timestamp e procedure approvate." },
      ],
      deployLabel: "Deployment",
      deployCaption: "Dentro il tuo perimetro. Sotto il tuo controllo.",
      compliance: "Roadmap di conformità",
    },
    team: {
      tag: "Leadership",
      h2: "Costruito da persone che conoscono la fabbrica.",
      sub: "Un team fondatore che unisce AI industriale, software enterprise e operations manifatturiere — ossessionato dal rendere l'intelligenza sicura per le infrastrutture critiche.",
      members: [
        { role: "Co-fondatore & CEO", bio: "Software enterprise e go-to-market industriale. Guida strategia, partnership e la rete di canali OEM." },
        { role: "Co-fondatore & CTO", bio: "AI industriale e sistemi edge. Architetto del motore Knowledge Graph e del confine data-blind." },
        { role: "Head of Product", bio: "Operations manifatturiere e design HMI. Responsabile dell'esperienza agent in linguaggio naturale in fabbrica." },
      ],
      onRequest: "Profilo su richiesta",
    },
    contact: {
      tag: "Inizia",
      h2: "Vedilo sui tuoi dati.",
      sub: "Prenota una demo tecnica — eseguiremo Streamline360 su un campione dei tuoi dati di impianto, on-premise, dentro il tuo perimetro.",
      bullets: ["Percorso di validazione tecnica di 60 giorni", "Si distribuisce all'edge o on-premise", "Si connette a historian, SCADA e CMMS nativamente"],
      name: "Nome completo",
      company: "Azienda",
      email: "Email di lavoro",
      message: "Cosa vorresti far analizzare a Streamline360? (opzionale)",
      submit: "Richiedi una demo",
      toastOk: "Richiesta ricevuta. Il nostro team ti contatterà a breve.",
      toastErr: "Qualcosa è andato storto. Riprova.",
      note: "I tuoi dati restano con noi. Nessun dato lascia il tuo stabilimento durante una demo.",
    },
    footer: {
      tagline: "AI agentica privata, on-premise, per la manifattura aziendale.",
      impressum: "Note legali",
      madeIn: "AI industriale privata per la manifattura aziendale — fatta a Copenaghen.",
      rights: "© 2026 Streamline360 ApS. Tutti i diritti riservati.",
      neverLeaves: "I tuoi dati non lasciano mai lo stabilimento.",
      privacy: "Informativa sulla privacy",
      terms: "Termini di utilizzo",
      contact: "Contatti",
    },
  },
  nl: {
    nav: { platform: "Platform", architecture: "Architectuur", benchmark: "Benchmark", security: "Beveiliging", team: "Team" },
    bookDemo: "Demo boeken",
    hero: {
      eyebrow: "Private industriële AI voor enterprise productie",
      line1: "Geef een taak.",
      line2: "Agenten doen het werk.",
      line3: "Elk inzicht traceerbaar.",
      sub: "Streamline360 is het agentische AI-platform gebouwd voor enterprise productie. Vraag het in gewone operationele taal — gespecialiseerde agenten halen live fabriekscontext uit je Knowledge Graph en antwoorden binnen seconden. On-premise en aan de edge: je data verlaat de fabriek nooit.",
      ctaBenchmark: "Bekijk de benchmark",
      stats: [
        { value: "90,2 %", label: "Benchmark-nauwkeurigheid" },
        { value: "~0,05 $", label: "Kosten per taak" },
        { value: "Seconden", label: "Tijd tot inzicht" },
        { value: "100 %", label: "Traceerbaar naar bron" },
      ],
    },
    marquee: ["NUL HALLUCINATIES", "100 % TRACEERBAAR", "KNOWLEDGE-GRAPH-GEBASEERD", "ON-PREMISE & EDGE", "VRAGEN IN DAGELIJKSE TAAL", "OPC-UA · MQTT · HISTORIAN-API'S"],
    problem: {
      tag: "Het probleem",
      h2: "Jouw fabriek kan de kracht van taalmodellen vandaag nog steeds niet benutten.",
      sub: "De wereldwijde industrie verliest miljarden door ongeplande stilstand — toch blijft generieke AI buiten de fabriekshal om drie structurele redenen.",
      cards: [
        { title: "De datasoevereiniteitsmuur", body: "Veiligheids- en kritieke-infrastructuurregels verbieden strikt het versturen van propriëtaire telemetrie en procesrecepten buiten de fabrieksperimeter. Cloud-AI is geen optie." },
        { title: "Legacy-datasilo's", body: "Cruciale fabriekskennis blijft gevangen in versnipperde historians, legacy SCADA-systemen, controlenetwerken en fysieke ploeglogboeken." },
        { title: "De pilotval", body: "Generieke frontier-modellen zijn te traag, te duur voor continue queries en vatbaar voor hallucinaties tijdens kritieke productiegebeurtenissen." },
      ],
    },
    stepper: {
      tag: "Van taak naar resultaat",
      h2: "Jij geeft een taak. Agenten doen de rest.",
      steps: [
        { n: "01", label: "Geef een taak", quote: "„Waarom is lijn 3 gisteren gestopt?”", body: "Fabriekspersoneel vraagt in standaard operationele taal. Geen queries schrijven, geen dashboards configureren, geen data exporteren." },
        { n: "02", label: "Agenten halen context", body: "Gespecialiseerde agenten halen fabrieksdata en relaties op uit de Knowledge Graph — alleen wat de taak nodig heeft." },
        { n: "03", label: "Agenten analyseren", body: "Stilstand, alarmen, parameters en trends worden geanalyseerd tegen geverifieerde fabriekscontext — nooit giswerk op ruwe tekst." },
        { n: "04", label: "Agenten produceren output", body: "Een dashboard wordt gebouwd, een hoofdoorzaak geïdentificeerd, aanbevelingen geformuleerd — elk onderbouwd met bewijs." },
        { n: "05", label: "Jij ontvangt het resultaat", body: "Inzichten, aanbevelingen en hoofdoorzaken binnen seconden geleverd — elk traceerbaar naar de bron, end-to-end." },
      ],
    },
    arch: {
      tag: "De architectuur",
      h2: "Eén Knowledge Graph. Een datablinde grens.",
      sub: "De lokale engine koppelt live machinetelemetrie aan de operationele kennis van je fabriek binnen een beveiligde perimeter. Ruwe data blijft binnen — alleen geabstraheerde context beweegt.",
      tour: [
        { label: "Stap 1 — Je data stroomt binnen", text: "Zeven stromen fabrieksintelligentie — historians, controlesystemen, MES, onderhoud, lab, documenten, ERP — stromen binnen als ruwe telemetrie." },
        { label: "Stap 2 — De datablinde grens", text: "Aan de perimeter worden ruwe payloads geabstraheerd tot operationele metadata. Niets raws kruist ooit." },
        { label: "Stap 3 — De Knowledge Graph", text: "Machinetelemetrie versmelt met menselijke kennis — SOP's, ploeglogs, fabrieksregels — volledig on-premise berekend." },
        { label: "Stap 4 — Gespecialiseerde agenten", text: "Agenten interpreteren je taak in operationele taal en halen alleen de context op die ze nodig hebben." },
        { label: "Stap 5 — Betrouwbare output", text: "Dashboards, hoofdoorzaken, alarm-inzichten en aanbevelingen stromen terug — elk traceerbaar naar de bron." },
        { label: "Stap 6 — De cloud blijft buiten", text: "Het pad naar frontier cloud-AI is geblokkeerd door architectuur, niet door beleid. Je telemetrie verlaat de fabriek nooit." },
      ],
      legend: ["Ruwe telemetrie — blijft in de fabriek", "Geabstraheerde metadata — agenten redeneren hierover", "Cloud-exfiltratie — geblokkeerd"],
      defaultDesc: "Beweeg over een databron, de grens of de Knowledge Graph om te volgen hoe data stroomt — en wat nooit vertrekt.",
      replay: "Tour opnieuw afspelen",
      connectors: "Connectoren",
      yourData: "Jouw data",
      runsLocally: "DRAAIT LOKAAL",
      blocked: "GEBLOKKEERD",
      kgMobileTitle: "Knowledge Graph",
      kgMobileBody: "Dual-stream-context achter een datablinde grens: live telemetrie versmolten met SOP's, ploeglogs en fabrieksregels. Ruwe data verlaat nooit de fabriek.",
      agentsTitle: "Gespecialiseerde agenten",
      outputsTitle: "Betrouwbare output",
      descs: {
        hub: "Dual-stream-context: live machinetelemetrie versmolten met de operationele kennis van je fabriek — volledig on-premise berekend.",
        boundary: "Ruwe payloads worden aan de perimeter geabstraheerd tot operationele metadata. Modellen draaien lokaal — niets wordt naar de cloud geëxfiltreerd.",
        agents: "Gespecialiseerde agenten interpreteren operationele intentie, halen alleen relevante context op en stellen antwoorden samen met volledige brontrace.",
        cloud: "Generieke cloud-AI zou vereisen dat ruwe telemetrie off-site wordt gestreamd. De grens blokkeert dat — door architectuur, niet door beleid.",
      },
      sourceDescs: [
        "Tijdreeks-procestrends stromen binnen als ruwe telemetrie — alleen geabstraheerde operationele metadata kruist de grens.",
        "Live controlesignalen en alarmen blijven op je controlenetwerk. Agenten redeneren over context, nooit over ruwe payloads.",
        "Productieorders, OEE-gebeurtenissen en werkvloerdata stromen binnen vanuit de MES-laag.",
        "Werkorders en onderhoudshistoriek verankeren elke hoofdoorzaakanalyse in wat er daadwerkelijk op de werkvloer is gedaan.",
        "Labresultaten versmelten met batchcontext zodat kwaliteitsafwijkingen direct correleren met procescondities.",
        "Bedieningshandleidingen en fabrieksregels begrenzen elke aanbeveling — niets overschrijdt je vooraf gevalideerde parameters.",
        "Orders, planningen en voorraad verbinden productiecontext met zakelijke prioriteiten.",
      ],
      outputDescs: [
        "Live operationele dashboards, op aanvraag gebouwd uit fabrieksdata.",
        "Afwijkingen en stilstand herleid tot hun specifieke oorzaak, met bewijs.",
        "Wat een alarm betekent, waarom het afging en wat er daarna te doen is.",
        "Actiegerichte vervolgstappen, gebonden aan gevalideerde bedrijfsparameters.",
      ],
    },
    deliver: {
      tag: "Wat je krijgt",
      h2: "Antwoorden, geen ruwe tekst.",
      liveExample: "Live voorbeeld — „Bouw een OEE-dashboard voor de persenafdeling.”",
      cards: [
        { title: "Dashboards", body: "Op aanvraag gebouwd, gevoed door live fabrieksdata — OEE, stilstand, kwaliteit, energie." },
        { title: "Hoofdoorzaakanalyse", body: "De specifieke reden achter een afwijking of stop — geïdentificeerd en onderbouwd." },
        { title: "Alarm-inzichten", body: "Wat een alarm betekent, waarom het afging en wat te doen." },
        { title: "Aanbevelingen", body: "Actiegerichte vervolgstappen — elk gebonden aan vooraf gevalideerde bedrijfsparameters." },
      ],
    },
    bench: {
      tag: "De benchmark",
      h2: "Presteert beter dan frontier-AI. Op jouw data.",
      sub: "Dezelfde taken, dezelfde fabrieksdatasets, naast elkaar — beoordeeld door onafhankelijke procesingenieurs. Gerichte retrieval verslaat ruwe-tekstverwerking op elke metriek.",
      stats: [
        { label: "Nauwkeurigheid", sub: "vs 15,8–17,7 % voor frontier-modellen" },
        { label: "Kosten per taak", sub: "vs tot ~1,80 $ per taak" },
        { label: "Tokens per taak", sub: "vs 500k+ voor frontier-modellen" },
      ],
      theirs: "Frontier-AI-modellen",
      bars: ["Nauwkeurigheid", "Kosten per taak", "Tokens per taak"],
      footnote: "Benchmark, augustus 2026 · identieke fabrieksdatasets · door mensen geverifieerde scoring. Lagere kosten- en tokenbalken zijn beter.",
    },
    security: {
      tag: "Beveiliging & deployment",
      h2: "Jouw telemetrie verlaat de fabriek nooit.",
      sub: "Streamline360 is privacy-first door architectuur, niet door beleid. De datablinde grens houdt propriëtaire recepten en procesdata binnen je beveiligde perimeter.",
      cards: [
        { title: "On-Premise & Edge", body: "Installeert direct aan de edge of in je fabrieksnetwerk. Geen cloud-afhankelijkheid, geen latentiebarrière." },
        { title: "Lokale datagrens", body: "Ruwe telemetrie wordt geabstraheerd tot operationele metadata. Modellen draaien lokaal — niets wordt geëxfiltreerd." },
        { title: "Nul hallucinaties", body: "Aanbevelingen zijn strikt gebonden aan je bedieningshandleidingen en vooraf gevalideerde parameters." },
        { title: "Volledige auditbaarheid", body: "Elk inzicht verwijst naar specifieke sensortags, tijdstempels en goedgekeurde procedures." },
      ],
      deployLabel: "Deployment",
      deployCaption: "Binnen jouw perimeter. Onder jouw controle.",
      compliance: "Compliance-roadmap",
    },
    team: {
      tag: "Leiderschap",
      h2: "Gebouwd door mensen die de fabrieksvloer kennen.",
      sub: "Een oprichtersteam dat industriële AI, enterprise-software en productieoperations bestrijkt — geobsedeerd door intelligentie veilig te maken voor kritieke infrastructuur.",
      members: [
        { role: "Medeoprichter & CEO", bio: "Enterprise-software en industriële go-to-market. Leidt strategie, partnerships en het OEM-kanaalnetwerk." },
        { role: "Medeoprichter & CTO", bio: "Industriële AI en edge-systemen. Architect van de Knowledge Graph-engine en de datablinde grens." },
        { role: "Head of Product", bio: "Productieoperations en HMI-design. Eigenaar van de agent-ervaring in dagelijkse taal op de werkvloer." },
      ],
      onRequest: "Profiel op aanvraag",
    },
    contact: {
      tag: "Aan de slag",
      h2: "Zie het op jouw data.",
      sub: "Boek een technische demo — we draaien Streamline360 op een steekproef van je fabrieksdata, on-premise, binnen jouw perimeter.",
      bullets: ["60-daags technisch validatietraject", "Deployt aan de edge of on-premise", "Verbindt out-of-the-box met historians, SCADA en CMMS"],
      name: "Volledige naam",
      company: "Bedrijf",
      email: "Zakelijk e-mailadres",
      message: "Wat moet Streamline360 analyseren? (optioneel)",
      submit: "Demo aanvragen",
      toastOk: "Aanvraag ontvangen. Ons team neemt snel contact op.",
      toastErr: "Er ging iets mis. Probeer het opnieuw.",
      note: "Je gegevens blijven bij ons. Tijdens een demo verlaat geen data je fabriek.",
    },
    footer: {
      tagline: "Private, on-premise agentische AI voor enterprise productie.",
      impressum: "Colofon",
      madeIn: "Private industriële AI voor enterprise productie — gemaakt in Kopenhagen.",
      rights: "© 2026 Streamline360 ApS. Alle rechten voorbehouden.",
      neverLeaves: "Je data verlaat de fabriek nooit.",
      privacy: "Privacybeleid",
      terms: "Gebruiksvoorwaarden",
      contact: "Contact",
    },
  },
  pt: {
    nav: { platform: "Plataforma", architecture: "Arquitetura", benchmark: "Benchmark", security: "Segurança", team: "Equipa" },
    bookDemo: "Agendar uma demo",
    hero: {
      eyebrow: "IA industrial privada para manufatura empresarial",
      line1: "Atribua uma tarefa.",
      line2: "Os agentes fazem o trabalho.",
      line3: "Cada insight rastreado.",
      sub: "Streamline360 é a plataforma de IA agêntica construída para a manufatura empresarial. Pergunte em linguagem operacional simples — agentes especializados extraem o contexto da planta em tempo real do seu Knowledge Graph e respondem em segundos. On-premise e na edge: os seus dados nunca saem da fábrica.",
      ctaBenchmark: "Ver o benchmark",
      stats: [
        { value: "90,2 %", label: "Precisão do benchmark" },
        { value: "~0,05 $", label: "Custo por tarefa" },
        { value: "Segundos", label: "Tempo até ao insight" },
        { value: "100 %", label: "Rastreável à fonte" },
      ],
    },
    marquee: ["ZERO ALUCINAÇÕES", "100 % RASTREÁVEL", "BASEADO EM KNOWLEDGE GRAPH", "ON-PREMISE & EDGE", "CONSULTAS EM LINGUAGEM NATURAL", "OPC-UA · MQTT · APIs DE HISTORIAN"],
    problem: {
      tag: "O problema",
      h2: "A sua fábrica ainda não consegue usar o poder dos modelos de linguagem.",
      sub: "A manufatura global perde milhares de milhões com paragens não planeadas — no entanto, a IA genérica continua fora da fábrica por três razões estruturais.",
      cards: [
        { title: "O muro da soberania de dados", body: "Regras de segurança e de infraestrutura crítica proíbem estritamente o envio de telemetria proprietária e receitas de processo para fora do perímetro da fábrica. IA na cloud não é opção." },
        { title: "Silos de dados legacy", body: "A inteligência crítica da fábrica fica presa em historians desconectados, sistemas SCADA legacy, redes de controlo e registos de turno em papel." },
        { title: "A armadilha do piloto", body: "Os modelos frontier genéricos são demasiado lentos, demasiado caros para consultas contínuas e propensos a alucinações durante eventos críticos de produção." },
      ],
    },
    stepper: {
      tag: "Da tarefa ao resultado",
      h2: "Você atribui uma tarefa. Os agentes tratam do resto.",
      steps: [
        { n: "01", label: "Atribua uma tarefa", quote: "«Porque é que a Linha 3 parou ontem?»", body: "A equipa pergunta em linguagem operacional padrão. Sem consultas para escrever, sem dashboards para configurar, sem dados para exportar." },
        { n: "02", label: "Os agentes extraem contexto", body: "Agentes especializados recuperam dados e relações da planta do Knowledge Graph — apenas o que a tarefa precisa." },
        { n: "03", label: "Os agentes analisam", body: "Paragens, alarmes, parâmetros e tendências são analisados contra o contexto verificado da planta — nunca suposições sobre texto bruto." },
        { n: "04", label: "Os agentes produzem o resultado", body: "Um dashboard é construído, uma causa raiz identificada, recomendações formuladas — cada uma com evidências." },
        { n: "05", label: "Recebe o resultado", body: "Insights, recomendações e causas raiz entregues em segundos — cada um rastreável à sua fonte, de ponta a ponta." },
      ],
    },
    arch: {
      tag: "A arquitetura",
      h2: "Um Knowledge Graph. Uma fronteira data-blind.",
      sub: "O motor local liga a telemetria das máquinas em tempo real ao conhecimento operacional da sua fábrica dentro de um perímetro seguro. Os dados brutos ficam dentro — só o contexto abstraído se move.",
      tour: [
        { label: "Passo 1 — Os seus dados entram", text: "Sete fluxos de inteligência de fábrica — historians, sistemas de controlo, MES, manutenção, laboratório, documentos, ERP — entram como telemetria bruta." },
        { label: "Passo 2 — A fronteira data-blind", text: "No perímetro, os payloads brutos são abstraídos em metadados operacionais. Nada bruto atravessa nunca." },
        { label: "Passo 3 — O Knowledge Graph", text: "A telemetria das máquinas funde-se com o conhecimento humano — SOPs, registos de turno, regras da fábrica — calculado inteiramente on-premise." },
        { label: "Passo 4 — Agentes especializados", text: "Os agentes interpretam a sua tarefa em linguagem operacional e recuperam apenas o contexto necessário." },
        { label: "Passo 5 — Resultados confiáveis", text: "Dashboards, causas raiz, insights de alarmes e recomendações regressam — cada um rastreável à fonte." },
        { label: "Passo 6 — A cloud fica de fora", text: "O caminho para a IA frontier na cloud está bloqueado pela arquitetura, não por políticas. A sua telemetria nunca sai da fábrica." },
      ],
      legend: ["Telemetria bruta — fica na fábrica", "Metadados abstraídos — base de raciocínio dos agentes", "Exfiltração para a cloud — bloqueada"],
      defaultDesc: "Passe o rato sobre uma fonte de dados, a fronteira ou o Knowledge Graph para seguir o fluxo de dados — e o que nunca sai.",
      replay: "Repetir a visita",
      connectors: "Conectores",
      yourData: "Os seus dados",
      runsLocally: "CORRE LOCALMENTE",
      blocked: "BLOQUEADO",
      kgMobileTitle: "Knowledge Graph",
      kgMobileBody: "Contexto dual-stream atrás de uma fronteira data-blind: telemetria em tempo real fundida com SOPs, registos de turno e regras da fábrica. Os dados brutos nunca saem.",
      agentsTitle: "Agentes especializados",
      outputsTitle: "Resultados confiáveis",
      descs: {
        hub: "Contexto dual-stream: telemetria das máquinas em tempo real fundida com o conhecimento operacional da fábrica — calculado inteiramente on-premise.",
        boundary: "Os payloads brutos são abstraídos em metadados operacionais no perímetro. Os modelos correm localmente — nada é exfiltrado para a cloud.",
        agents: "Agentes especializados interpretam a intenção operacional, recuperam apenas o contexto relevante e constroem respostas com rasto completo das fontes.",
        cloud: "A IA genérica na cloud exigiria transmitir telemetria bruta para fora do local. A fronteira bloqueia isso — por arquitetura, não por política.",
      },
      sourceDescs: [
        "Tendências de processo em séries temporais entram como telemetria bruta — apenas metadados operacionais abstraídos atravessam a fronteira.",
        "Sinais de controlo e alarmes em tempo real ficam na sua rede de controlo. Os agentes raciocinam sobre contexto, nunca sobre payloads brutos.",
        "Ordens de produção, eventos OEE e dados de execução de chão de fábrica entram pela camada MES.",
        "Ordens de trabalho e histórico de manutenção ancoram cada análise de causa raiz no que foi realmente feito no chão de fábrica.",
        "Resultados de laboratório fundem-se com o contexto de lote para correlacionar desvios de qualidade com condições de processo.",
        "Manuais de operação e regras da fábrica limitam cada recomendação — nada excede os seus parâmetros pré-validados.",
        "Encomendas, planeamentos e inventário ligam o contexto de produção às prioridades do negócio.",
      ],
      outputDescs: [
        "Dashboards operacionais em tempo real, construídos a pedido a partir de dados da fábrica.",
        "Desvios e paragens rastreados até à causa específica, com evidências.",
        "O que um alarme significa, porque disparou e o que fazer a seguir.",
        "Próximos passos acionáveis, ligados a parâmetros operacionais validados.",
      ],
    },
    deliver: {
      tag: "O que recebe",
      h2: "Respostas, não texto bruto.",
      liveExample: "Exemplo ao vivo — «Constrói um dashboard OEE para a seção de prensas.»",
      cards: [
        { title: "Dashboards", body: "Construídos a pedido, alimentados por dados da fábrica em tempo real — OEE, paragens, qualidade, energia." },
        { title: "Análise de causa raiz", body: "A razão específica por trás de um desvio ou paragem — identificada e documentada." },
        { title: "Insights de alarmes", body: "O que um alarme significa, porque disparou e o que fazer." },
        { title: "Recomendações", body: "Próximos passos acionáveis — cada um ligado a parâmetros operacionais pré-validados." },
      ],
    },
    bench: {
      tag: "O benchmark",
      h2: "Supera a IA frontier. Com os seus dados.",
      sub: "As mesmas tarefas, os mesmos conjuntos de dados de fábrica, lado a lado — avaliados por engenheiros de processo independentes. A recuperação focada vence o processamento de texto bruto em cada métrica.",
      stats: [
        { label: "Precisão", sub: "vs 15,8–17,7 % nos modelos frontier" },
        { label: "Custo por tarefa", sub: "vs até ~1,80 $ por tarefa" },
        { label: "Tokens por tarefa", sub: "vs 500k+ nos modelos frontier" },
      ],
      theirs: "Modelos de IA frontier",
      bars: ["Precisão", "Custo por tarefa", "Tokens por tarefa"],
      footnote: "Benchmark, agosto de 2026 · conjuntos de dados de fábrica idênticos · pontuação verificada por humanos. Barras de custo e tokens mais baixas são melhores.",
    },
    security: {
      tag: "Segurança & implementação",
      h2: "A sua telemetria nunca sai da fábrica.",
      sub: "Streamline360 é privacy-first por arquitetura, não por política. A fronteira data-blind mantém receitas proprietárias e dados de processo dentro do seu perímetro seguro.",
      cards: [
        { title: "On-Premise & Edge", body: "Instala diretamente na edge ou na sua rede de fábrica. Sem dependência da cloud, sem barreira de latência." },
        { title: "Fronteira de dados local", body: "A telemetria bruta é abstraída em metadados operacionais. Os modelos correm localmente — nada é exfiltrado." },
        { title: "Zero alucinações", body: "As recomendações estão estritamente ligadas aos seus manuais de operação e parâmetros pré-validados." },
        { title: "Auditabilidade completa", body: "Cada insight remete para tags de sensores específicas, carimbos temporais e procedimentos aprovados." },
      ],
      deployLabel: "Implementação",
      deployCaption: "Dentro do seu perímetro. Sob o seu controlo.",
      compliance: "Roteiro de conformidade",
    },
    team: {
      tag: "Liderança",
      h2: "Construído por pessoas que conhecem o chão de fábrica.",
      sub: "Uma equipa fundadora que abrange IA industrial, software empresarial e operações de manufatura — obcecada em tornar a inteligência segura para infraestruturas críticas.",
      members: [
        { role: "Cofundador & CEO", bio: "Software empresarial e go-to-market industrial. Lidera estratégia, parcerias e a rede de canais OEM." },
        { role: "Cofundador & CTO", bio: "IA industrial e sistemas edge. Arquiteto do motor Knowledge Graph e da fronteira data-blind." },
        { role: "Head of Product", bio: "Operações de manufatura e design HMI. Responsável pela experiência de agentes em linguagem natural na fábrica." },
      ],
      onRequest: "Perfil a pedido",
    },
    contact: {
      tag: "Começar",
      h2: "Veja com os seus dados.",
      sub: "Agende uma demo técnica — executamos o Streamline360 contra uma amostra dos dados da sua fábrica, on-premise, dentro do seu perímetro.",
      bullets: ["Percurso de validação técnica de 60 dias", "Implementa na edge ou on-premise", "Liga-se a historians, SCADA e CMMS de raiz"],
      name: "Nome completo",
      company: "Empresa",
      email: "E-mail profissional",
      message: "O que gostaria que o Streamline360 analisasse? (opcional)",
      submit: "Pedir uma demo",
      toastOk: "Pedido recebido. A nossa equipa entrará em contacto em breve.",
      toastErr: "Algo correu mal. Tente novamente.",
      note: "Os seus dados ficam connosco. Nenhum dado sai da sua fábrica durante uma demo.",
    },
    footer: {
      tagline: "IA agêntica privada, on-premise, para manufatura empresarial.",
      impressum: "Ficha técnica",
      madeIn: "IA industrial privada para manufatura empresarial — feita em Copenhaga.",
      rights: "© 2026 Streamline360 ApS. Todos os direitos reservados.",
      neverLeaves: "Os seus dados nunca saem da fábrica.",
      privacy: "Política de privacidade",
      terms: "Termos de utilização",
      contact: "Contacto",
    },
  },
  sv: {
    nav: { platform: "Plattform", architecture: "Arkitektur", benchmark: "Benchmark", security: "Säkerhet", team: "Team" },
    bookDemo: "Boka en demo",
    hero: {
      eyebrow: "Privat industriell AI för företagstillverkning",
      line1: "Tilldela en uppgift.",
      line2: "Agenter gör jobbet.",
      line3: "Varje insikt spårbar.",
      sub: "Streamline360 är den agentiska AI-plattformen byggd för företagstillverkning. Fråga på vanligt driftsspråk — specialiserade agenter hämtar live anläggningskontext från din Knowledge Graph och svarar på sekunder. On-premise och på edge: din data lämnar aldrig fabriken.",
      ctaBenchmark: "Se benchmarken",
      stats: [
        { value: "90,2 %", label: "Benchmark-noggrannhet" },
        { value: "~0,05 $", label: "Kostnad per uppgift" },
        { value: "Sekunder", label: "Tid till insikt" },
        { value: "100 %", label: "Spårbar till källan" },
      ],
    },
    marquee: ["NOLL HALLUCINATIONER", "100 % SPÅRBART", "KNOWLEDGE-GRAPH-BASERAT", "ON-PREMISE & EDGE", "FRÅGOR PÅ VARDAGSSPRÅK", "OPC-UA · MQTT · HISTORIAN-APIER"],
    problem: {
      tag: "Problemet",
      h2: "Din fabrik kan fortfarande inte använda språkmodellernas kraft idag.",
      sub: "Den globala tillverkningen förlorar miljarder på oplanerade stopp — ändå hålls generisk AI ute från fabriksgolvet av tre strukturella skäl.",
      cards: [
        { title: "Datasuveränitetsmuren", body: "Säkerhets- och kritisk-infrastrukturregler förbjuder strikt att skicka proprietär telemetri och processrecept utanför fabrikens perimeter. Moln-AI är uteslutet." },
        { title: "Äldre datasiloar", body: "Avgörande fabriksintelligens fastnar i splittrade historians, äldre SCADA-system, styrnätverk och fysiska skiftloggar." },
        { title: "Pilotfällan", body: "Generiska frontier-modeller är för långsamma, för dyra för kontinuerliga frågor och benägna att hallucinera vid kritiska produktionshändelser." },
      ],
    },
    stepper: {
      tag: "Från uppgift till resultat",
      h2: "Du tilldelar en uppgift. Agenter sköter resten.",
      steps: [
        { n: "01", label: "Tilldela en uppgift", quote: "”Varför stannade linje 3 igår?”", body: "Fabrikspersonal frågar på vanligt driftsspråk. Inga frågor att skriva, inga dashboards att konfigurera, ingen data att exportera." },
        { n: "02", label: "Agenter hämtar kontext", body: "Specialiserade agenter hämtar anläggningsdata och relationer från Knowledge Graph — bara det uppgiften behöver." },
        { n: "03", label: "Agenter analyserar", body: "Stopp, larm, parametrar och trender analyseras mot verifierad anläggningskontext — aldrig gissningar på rå text." },
        { n: "04", label: "Agenter producerar resultat", body: "En dashboard byggs, en rotorsak identifieras, rekommendationer formuleras — var och en med underlag." },
        { n: "05", label: "Du får resultatet", body: "Insikter, rekommendationer och rotorsaker levererade på sekunder — var och en spårbar till sin källa, från början till slut." },
      ],
    },
    arch: {
      tag: "Arkitekturen",
      h2: "En Knowledge Graph. En datablind gräns.",
      sub: "Den lokala motorn kopplar live maskintelemetri till fabrikens driftskunskap inom en säker perimeter. Rådata stannar inne — bara abstraherad kontext rör sig.",
      tour: [
        { label: "Steg 1 — Din data flödar in", text: "Sju strömmar av fabriksintelligens — historians, styrsystem, MES, underhåll, labb, dokument, ERP — flödar in som rå telemetri." },
        { label: "Steg 2 — Den datablinda gränsen", text: "Vid perimetern abstraheras råa payloads till operativa metadata. Inget rått korsar någonsin." },
        { label: "Steg 3 — Knowledge Graph", text: "Maskintelemetri smälter samman med mänsklig driftskunskap — SOP:ar, skiftloggar, fabriksregler — beräknat helt on-premise." },
        { label: "Steg 4 — Specialiserade agenter", text: "Agenter tolkar din uppgift på driftsspråk och hämtar bara den kontext de behöver." },
        { label: "Steg 5 — Pålitliga resultat", text: "Dashboards, rotorsaker, larminsikter och rekommendationer flödar tillbaka — var och en spårbar till källan." },
        { label: "Steg 6 — Molnet stannar ute", text: "Vägen till frontier moln-AI är blockerad av arkitekturen, inte av policy. Din telemetri lämnar aldrig fabriken." },
      ],
      legend: ["Rå telemetri — stannar i fabriken", "Abstraherad metadata — agenternas resonemangsgrund", "Molnexfiltrering — blockerad"],
      defaultDesc: "Håll muspekaren över en datakälla, gränsen eller Knowledge Graph för att följa dataflödet — och vad som aldrig lämnar fabriken.",
      replay: "Spela turen igen",
      connectors: "Kopplingar",
      yourData: "Din data",
      runsLocally: "KÖRS LOKALT",
      blocked: "BLOCKERAD",
      kgMobileTitle: "Knowledge Graph",
      kgMobileBody: "Dual-stream-kontext bakom en datablind gräns: live telemetri sammansmält med SOP:ar, skiftloggar och fabriksregler. Rådata lämnar aldrig fabriken.",
      agentsTitle: "Specialiserade agenter",
      outputsTitle: "Pålitliga resultat",
      descs: {
        hub: "Dual-stream-kontext: live maskintelemetri sammansmält med fabrikens driftskunskap — beräknad helt on-premise.",
        boundary: "Råa payloads abstraheras till operativa metadata vid perimetern. Modeller körs lokalt — inget exfiltreras till molnet.",
        agents: "Specialiserade agenter tolkar operativ avsikt, hämtar bara relevant kontext och sätter samman svar med fullständig källspårning.",
        cloud: "Generisk moln-AI skulle kräva att rå telemetri streamas utanför anläggningen. Gränsen blockerar det — genom arkitektur, inte policy.",
      },
      sourceDescs: [
        "Tidsserie-processtrender flödar in som rå telemetri — bara abstraherad operativ metadata korsar gränsen.",
        "Live styrsignaler och larm stannar i ditt styrnätverk. Agenter resonerar om kontext, aldrig råa payloads.",
        "Produktionsorder, OEE-händelser och verkstadsdata strömmar in från MES-lagret.",
        "Arbetsorder och underhållshistorik förankrar varje rotorsaksanalys i vad som faktiskt gjordes på golvet.",
        "Labbresultat smälter samman med batchkontext så att kvalitetsavvikelser korrelerar direkt med processförhållanden.",
        "Driftsmanualer och fabriksregler begränsar varje rekommendation — inget överskrider dina förvaliderade parametrar.",
        "Ordrar, scheman och lager kopplar produktionskontext till affärsprioriteringar.",
      ],
      outputDescs: [
        "Live drift-dashboards, byggda på begäran från anläggningsdata.",
        "Avvikelser och stopp spårade till sin specifika orsak, med underlag.",
        "Vad ett larm betyder, varför det utlöstes och vad som ska göras.",
        "Handlingsbara nästa steg, bundna till validerade driftsparametrar.",
      ],
    },
    deliver: {
      tag: "Vad du får",
      h2: "Svar, inte rå text.",
      liveExample: "Live-exempel — ”Bygg en OEE-dashboard för pressverkstaden.”",
      cards: [
        { title: "Dashboards", body: "Byggda på begäran, matade från live anläggningsdata — OEE, stopp, kvalitet, energi." },
        { title: "Rotorsaksanalys", body: "Den specifika orsaken bakom en avvikelse eller ett stopp — identifierad och dokumenterad." },
        { title: "Larminsikter", body: "Vad ett larm betyder, varför det utlöstes och vad man ska göra." },
        { title: "Rekommendationer", body: "Handlingsbara nästa steg — var och en bunden till förvaliderade driftsparametrar." },
      ],
    },
    bench: {
      tag: "Benchmarken",
      h2: "Överträffar frontier-AI. På din data.",
      sub: "Samma uppgifter, samma fabriksdataset, sida vid sida — bedömt av oberoende processingenjörer. Fokuserad hämtning slår rå-textbearbetning på varje mått.",
      stats: [
        { label: "Noggrannhet", sub: "vs 15,8–17,7 % för frontier-modeller" },
        { label: "Kostnad per uppgift", sub: "vs upp till ~1,80 $ per uppgift" },
        { label: "Tokens per uppgift", sub: "vs 500k+ för frontier-modeller" },
      ],
      theirs: "Frontier-AI-modeller",
      bars: ["Noggrannhet", "Kostnad per uppgift", "Tokens per uppgift"],
      footnote: "Benchmark, augusti 2026 · identiska fabriksdataset · människoverifierad bedömning. Lägre kostnads- och tokenstaplar är bättre.",
    },
    security: {
      tag: "Säkerhet & driftsättning",
      h2: "Din telemetri lämnar aldrig fabriken.",
      sub: "Streamline360 är privacy-first genom arkitektur, inte policy. Den datablinda gränsen håller proprietära recept och processdata inom din säkra perimeter.",
      cards: [
        { title: "On-Premise & Edge", body: "Installeras direkt på edge eller i ditt fabriksnätverk. Inget molnberoende, ingen latensbarriär." },
        { title: "Lokal datagräns", body: "Rå telemetri abstraheras till operativa metadata. Modeller körs lokalt — inget exfiltreras." },
        { title: "Noll hallucinationer", body: "Rekommendationer är strikt bundna till dina driftsmanualer och förvaliderade parametrar." },
        { title: "Fullständig revision", body: "Varje insikt länkar till specifika sensortaggar, tidsstämplar och godkända procedurer." },
      ],
      deployLabel: "Driftsättning",
      deployCaption: "Inom din perimeter. Under din kontroll.",
      compliance: "Compliance-färdplan",
    },
    team: {
      tag: "Ledning",
      h2: "Byggt av människor som känner fabriksgolvet.",
      sub: "Ett grundarteam som spänner över industriell AI, enterprise-mjukvara och tillverkningsdrift — besatt av att göra intelligens säker för kritisk infrastruktur.",
      members: [
        { role: "Medgrundare & CEO", bio: "Enterprise-mjukvara och industriell go-to-market. Leder strategi, partnerskap och OEM-kanalnätverket." },
        { role: "Medgrundare & CTO", bio: "Industriell AI och edge-system. Arkitekten bakom Knowledge Graph-motorn och den datablinda gränsen." },
        { role: "Head of Product", bio: "Tillverkningsdrift och HMI-design. Ansvarar för agentupplevelsen på vardagsspråk på fabriksgolvet." },
      ],
      onRequest: "Profil på begäran",
    },
    contact: {
      tag: "Kom igång",
      h2: "Se det på din data.",
      sub: "Boka en teknisk demo — vi kör Streamline360 mot ett urval av din anläggningsdata, on-premise, inom din perimeter.",
      bullets: ["60-dagars teknisk valideringsbana", "Driftsätts på edge eller on-premise", "Kopplar till historians, SCADA och CMMS direkt"],
      name: "Fullständigt namn",
      company: "Företag",
      email: "Jobbmejl",
      message: "Vad vill du att Streamline360 ska analysera? (valfritt)",
      submit: "Begär en demo",
      toastOk: "Förfrågan mottagen. Vårt team återkommer inom kort.",
      toastErr: "Något gick fel. Försök igen.",
      note: "Dina uppgifter stannar hos oss. Ingen data lämnar din fabrik under en demo.",
    },
    footer: {
      tagline: "Privat, on-premise agentisk AI för företagstillverkning.",
      impressum: "Impressum",
      madeIn: "Privat industriell AI för företagstillverkning — gjord i Köpenhamn.",
      rights: "© 2026 Streamline360 ApS. Alla rättigheter förbehållna.",
      neverLeaves: "Din data lämnar aldrig fabriken.",
      privacy: "Integritetspolicy",
      terms: "Användarvillkor",
      contact: "Kontakt",
    },
  },
  pl: {
    nav: { platform: "Platforma", architecture: "Architektura", benchmark: "Benchmark", security: "Bezpieczeństwo", team: "Zespół" },
    bookDemo: "Umów demo",
    hero: {
      eyebrow: "Prywatna przemysłowa AI dla produkcji korporacyjnej",
      line1: "Zadaj zadanie.",
      line2: "Agenci wykonają pracę.",
      line3: "Każdy wgląd identyfikowalny.",
      sub: "Streamline360 to platforma agentycznej AI zbudowana dla produkcji korporacyjnej. Pytaj prostym językiem operacyjnym — wyspecjalizowani agenci pobierają żywy kontekst zakładu z Twojego Knowledge Graph i odpowiadają w kilka sekund. On-premise i na edge: Twoje dane nigdy nie opuszczają zakładu.",
      ctaBenchmark: "Zobacz benchmark",
      stats: [
        { value: "90,2 %", label: "Dokładność benchmarku" },
        { value: "~0,05 $", label: "Koszt zadania" },
        { value: "Sekundy", label: "Czas do wglądu" },
        { value: "100 %", label: "Identyfikowalne do źródła" },
      ],
    },
    marquee: ["ZERO HALUCYNACJI", "100 % IDENTYFIKOWALNOŚCI", "OPARTE NA KNOWLEDGE GRAPH", "ON-PREMISE & EDGE", "ZAPYTANIA PROSTYM JĘZYKIEM", "OPC-UA · MQTT · API HISTORIANÓW"],
    problem: {
      tag: "Problem",
      h2: "Twój zakład wciąż nie może korzystać z mocy modeli językowych.",
      sub: "Światowa produkcja traci miliardy przez nieplanowane przestoje — a generyczna AI pozostaje poza halą z trzech strukturalnych powodów.",
      cards: [
        { title: "Mur suwerenności danych", body: "Przepisy bezpieczeństwa i infrastruktury krytycznej surowo zakazują wysyłania własnościowej telemetrii i receptur procesowych poza obręb zakładu. Chmurowa AI odpada." },
        { title: "Silosy danych legacy", body: "Kluczowa wiedza fabryczna pozostaje uwięziona w rozproszonych historianach, starszych systemach SCADA, sieciach sterowania i papierowych dziennikach zmian." },
        { title: "Pułapka pilotażu", body: "Generyczne modele frontier są zbyt wolne, zbyt drogie do ciągłych zapytań i skłonne do halucynacji podczas krytycznych zdarzeń produkcyjnych." },
      ],
    },
    stepper: {
      tag: "Od zadania do wyniku",
      h2: "Ty zadajesz zadanie. Agenci zajmują się resztą.",
      steps: [
        { n: "01", label: "Zadaj zadanie", quote: "„Dlaczego linia 3 wczoraj się zatrzymała?”", body: "Pracownicy pytają zwykłym językiem operacyjnym. Bez pisania zapytań, konfigurowania dashboardów i eksportowania danych." },
        { n: "02", label: "Agenci pobierają kontekst", body: "Wyspecjalizowani agenci pobierają dane i relacje zakładu z Knowledge Graph — tylko to, czego zadanie wymaga." },
        { n: "03", label: "Agenci analizują", body: "Przestoje, alarmy, parametry i trendy są analizowane wobec zweryfikowanego kontekstu zakładu — nigdy zgadywanie na surowym tekście." },
        { n: "04", label: "Agenci tworzą wynik", body: "Powstaje dashboard, zidentyfikowana zostaje przyczyna źródłowa, formułowane rekomendacje — każda poparta dowodami." },
        { n: "05", label: "Otrzymujesz wynik", body: "Wglądy, rekomendacje i przyczyny źródłowe w kilka sekund — każdy identyfikowalny do źródła, od końca do końca." },
      ],
    },
    arch: {
      tag: "Architektura",
      h2: "Jeden Knowledge Graph. Ślepa na dane granica.",
      sub: "Lokalny silnik łączy żywą telemetrię maszyn z wiedzą operacyjną Twojego zakładu w bezpiecznym obrębie. Surowe dane zostają w środku — porusza się tylko abstrahowany kontekst.",
      tour: [
        { label: "Krok 1 — Twoje dane napływają", text: "Siedem strumieni inteligencji zakładu — historiany, systemy sterowania, MES, utrzymanie, laboratorium, dokumenty, ERP — napływa jako surowa telemetria." },
        { label: "Krok 2 — Ślepa na dane granica", text: "Na obrębie surowe payloady są abstrahowane do metadanych operacyjnych. Nic surowego nigdy nie przechodzi." },
        { label: "Krok 3 — Knowledge Graph", text: "Telemetria maszyn stapia się z ludzką wiedzą — SOP, dzienniki zmian, reguły zakładu — obliczana w całości on-premise." },
        { label: "Krok 4 — Wyspecjalizowani agenci", text: "Agenci interpretują Twoje zadanie w języku operacyjnym i pobierają tylko potrzebny kontekst." },
        { label: "Krok 5 — Zaufane wyniki", text: "Dashboardy, przyczyny źródłowe, wglądy w alarmy i rekomendacje wracają — każdy identyfikowalny do źródła." },
        { label: "Krok 6 — Chmura zostaje na zewnątrz", text: "Ścieżka do chmurowej frontier AI jest zablokowana architekturą, nie polityką. Twoja telemetria nigdy nie opuszcza zakładu." },
      ],
      legend: ["Surowa telemetria — zostaje w zakładzie", "Abstrahowane metadane — podstawa rozumowania agentów", "Eksfiltracja do chmury — zablokowana"],
      defaultDesc: "Najedź na źródło danych, granicę lub Knowledge Graph, aby prześledzić przepływ danych — i to, co nigdy nie wypływa.",
      replay: "Odtwórz trasę ponownie",
      connectors: "Konektory",
      yourData: "Twoje dane",
      runsLocally: "DZIAŁA LOKALNIE",
      blocked: "ZABLOKOWANE",
      kgMobileTitle: "Knowledge Graph",
      kgMobileBody: "Kontekst dual-stream za ślepą na dane granicą: żywa telemetria stopiona z SOP, dziennikami zmian i regułami zakładu. Surowe dane nigdy nie wychodzą.",
      agentsTitle: "Wyspecjalizowani agenci",
      outputsTitle: "Zaufane wyniki",
      descs: {
        hub: "Kontekst dual-stream: żywa telemetria maszyn stopiona z wiedzą operacyjną zakładu — obliczana w całości on-premise.",
        boundary: "Surowe payloady są abstrahowane do metadanych operacyjnych na obrębie. Modele działają lokalnie — nic nie jest eksfiltrowane do chmury.",
        agents: "Wyspecjalizowani agenci interpretują intencję operacyjną, pobierają tylko istotny kontekst i składają odpowiedzi z pełnym śladem źródeł.",
        cloud: "Generyczna chmurowa AI wymagałaby strumieniowania surowej telemetrii poza zakład. Granica to blokuje — architekturą, nie polityką.",
      },
      sourceDescs: [
        "Szeregi czasowe trendów procesowych napływają jako surowa telemetria — tylko abstrahowane metadane operacyjne przekraczają granicę.",
        "Żywe sygnały sterowania i alarmy zostają w Twojej sieci sterowania. Agenci rozumują o kontekście, nigdy o surowych payloadach.",
        "Zlecenia produkcyjne, zdarzenia OEE i dane wykonawcze z hali napływają z warstwy MES.",
        "Zlecenia pracy i historia utrzymania ugruntowują każdą analizę przyczyn źródłowych w tym, co faktycznie zrobiono na hali.",
        "Wyniki laboratoryjne stapiają się z kontekstem partii, więc odchylenia jakości korelują bezpośrednio z warunkami procesu.",
        "Instrukcje obsługi i reguły zakładu ograniczają każdą rekomendację — nic nie przekracza Twoich zwalidowanych parametrów.",
        "Zamówienia, harmonogramy i zapasy łączą kontekst produkcji z priorytetami biznesowymi.",
      ],
      outputDescs: [
        "Żywe dashboardy operacyjne, budowane na żądanie z danych zakładu.",
        "Odchylenia i przestoje prześledzone do konkretnej przyczyny, z dowodami.",
        "Co alarm oznacza, dlaczego się uruchomił i co zrobić dalej.",
        "Wykonalne kolejne kroki, powiązane ze zwalidowanymi parametrami operacyjnymi.",
      ],
    },
    deliver: {
      tag: "Co otrzymujesz",
      h2: "Odpowiedzi, nie surowy tekst.",
      liveExample: "Przykład na żywo — „Zbuduj dashboard OEE dla wydziału pras.”",
      cards: [
        { title: "Dashboardy", body: "Budowane na żądanie, zasilane żywymi danymi zakładu — OEE, przestoje, jakość, energia." },
        { title: "Analiza przyczyn źródłowych", body: "Konkretny powód odchylenia lub przestoju — zidentyfikowany i udokumentowany." },
        { title: "Wglądy w alarmy", body: "Co alarm oznacza, dlaczego się uruchomił i co zrobić." },
        { title: "Rekomendacje", body: "Wykonalne kolejne kroki — każdy powiązany ze zwalidowanymi parametrami operacyjnymi." },
      ],
    },
    bench: {
      tag: "Benchmark",
      h2: "Przewyższa frontier AI. Na Twoich danych.",
      sub: "Te same zadania, te same zbiory danych fabrycznych, obok siebie — oceniane przez niezależnych inżynierów procesu. Ukierunkowane pobieranie bije przetwarzanie surowego tekstu w każdej metryce.",
      stats: [
        { label: "Dokładność", sub: "vs 15,8–17,7 % modeli frontier" },
        { label: "Koszt zadania", sub: "vs do ~1,80 $ za zadanie" },
        { label: "Tokeny na zadanie", sub: "vs 500k+ modeli frontier" },
      ],
      theirs: "Modele frontier AI",
      bars: ["Dokładność", "Koszt zadania", "Tokeny na zadanie"],
      footnote: "Benchmark, sierpień 2026 · identyczne zbiory danych fabrycznych · ocena zweryfikowana przez ludzi. Niższe słupki kosztu i tokenów są lepsze.",
    },
    security: {
      tag: "Bezpieczeństwo i wdrożenie",
      h2: "Twoja telemetria nigdy nie opuszcza zakładu.",
      sub: "Streamline360 to privacy-first przez architekturę, nie politykę. Ślepa na dane granica trzyma własnościowe receptury i dane procesowe w Twoim bezpiecznym obrębie.",
      cards: [
        { title: "On-Premise & Edge", body: "Instaluje się bezpośrednio na edge lub w sieci zakładu. Bez zależności od chmury, bez bariery opóźnień." },
        { title: "Lokalna granica danych", body: "Surowa telemetria jest abstrahowana do metadanych operacyjnych. Modele działają lokalnie — nic nie jest eksfiltrowane." },
        { title: "Zero halucynacji", body: "Rekomendacje są ściśle powiązane z Twoimi instrukcjami obsługi i zwalidowanymi parametrami." },
        { title: "Pełna audytowalność", body: "Każdy wgląd odsyła do konkretnych tagów czujników, znaczników czasu i zatwierdzonych procedur." },
      ],
      deployLabel: "Wdrożenie",
      deployCaption: "W Twoim obrębie. Pod Twoją kontrolą.",
      compliance: "Mapa zgodności",
    },
    team: {
      tag: "Kierownictwo",
      h2: "Zbudowane przez ludzi, którzy znają halę produkcyjną.",
      sub: "Zespół założycielski łączący przemysłową AI, oprogramowanie enterprise i operacje produkcyjne — zaabsorbowany uczynieniem inteligencji bezpieczną dla infrastruktury krytycznej.",
      members: [
        { role: "Współzałożyciel & CEO", bio: "Oprogramowanie enterprise i przemysłowy go-to-market. Prowadzi strategię, partnerstwa i sieć kanałów OEM." },
        { role: "Współzałożyciel & CTO", bio: "Przemysłowa AI i systemy edge. Architekt silnika Knowledge Graph i ślepej na dane granicy." },
        { role: "Head of Product", bio: "Operacje produkcyjne i projektowanie HMI. Odpowiada za doświadczenie agentów w prostym języku na hali." },
      ],
      onRequest: "Profil na życzenie",
    },
    contact: {
      tag: "Zacznij",
      h2: "Zobacz to na swoich danych.",
      sub: "Umów demo techniczne — uruchomimy Streamline360 na próbce danych Twojego zakładu, on-premise, w Twoim obrębie.",
      bullets: ["60-dniowa ścieżka walidacji technicznej", "Wdrażany na edge lub on-premise", "Łączy się z historianami, SCADA i CMMS od razu"],
      name: "Imię i nazwisko",
      company: "Firma",
      email: "Służbowy e-mail",
      message: "Co Streamline360 ma analizować? (opcjonalnie)",
      submit: "Poproś o demo",
      toastOk: "Prośba otrzymana. Nasz zespół wkrótce się odezwie.",
      toastErr: "Coś poszło nie tak. Spróbuj ponownie.",
      note: "Twoje dane zostają u nas. Podczas demo żadne dane nie opuszczają Twojego zakładu.",
    },
    footer: {
      tagline: "Prywatna, on-premise agentyczna AI dla produkcji korporacyjnej.",
      impressum: "Impressum",
      madeIn: "Prywatna przemysłowa AI dla produkcji korporacyjnej — stworzona w Kopenhadze.",
      rights: "© 2026 Streamline360 ApS. Wszelkie prawa zastrzeżone.",
      neverLeaves: "Twoje dane nigdy nie opuszczają zakładu.",
      privacy: "Polityka prywatności",
      terms: "Warunki korzystania",
      contact: "Kontakt",
    },
  },
  zh: {
    nav: { platform: "平台", architecture: "架构", benchmark: "基准测试", security: "安全", team: "团队" },
    bookDemo: "预约演示",
    hero: {
      eyebrow: "面向企业制造的私有工业AI",
      line1: "下达任务。",
      line2: "智能体完成工作。",
      line3: "每个洞察可追溯。",
      sub: "Streamline360是专为企业制造打造的智能体AI平台。用日常操作语言提问——专业智能体从您的知识图谱中提取实时工厂上下文,秒级返回答案。本地与边缘部署:数据永不离开工厂。",
      ctaBenchmark: "查看基准测试",
      stats: [
        { value: "90.2%", label: "基准准确率" },
        { value: "~$0.05", label: "每任务成本" },
        { value: "数秒", label: "获得洞察的时间" },
        { value: "100%", label: "可追溯至源头" },
      ],
    },
    marquee: ["零幻觉", "100%可追溯", "知识图谱驱动", "本地与边缘部署", "自然语言查询", "OPC-UA · MQTT · HISTORIAN API"],
    problem: {
      tag: "问题所在",
      h2: "您的工厂今天仍然无法使用语言模型的力量。",
      sub: "全球制造业因计划外停机损失数十亿——然而通用AI因三个结构性原因始终无法进入车间。",
      cards: [
        { title: "数据主权之墙", body: "安全和关键基础设施法规严禁将专有遥测和工艺配方发送到工厂边界之外。云AI无从谈起。" },
        { title: "传统数据孤岛", body: "关键的工厂智能被困在分散的历史数据库、老旧SCADA系统、控制网络和纸质班次日志中。" },
        { title: "试点陷阱", body: "通用前沿模型太慢、持续查询成本太高,且在关键生产事件中容易产生幻觉。" },
      ],
    },
    stepper: {
      tag: "从任务到结果",
      h2: "您下达任务,智能体完成其余工作。",
      steps: [
        { n: "01", label: "下达任务", quote: "“3号产线昨天为什么停机?”", body: "工厂员工用标准操作语言提问。无需编写查询、配置仪表板或导出数据。" },
        { n: "02", label: "智能体提取上下文", body: "专业智能体从知识图谱中检索工厂数据和关系——只取任务所需。" },
        { n: "03", label: "智能体分析", body: "停机、报警、参数和趋势都对照经过验证的工厂上下文进行分析——绝不对原始文本臆测。" },
        { n: "04", label: "智能体生成输出", body: "构建仪表板、识别根本原因、形成建议——每一项都有证据支持。" },
        { n: "05", label: "您获得结果", body: "洞察、建议和根本原因在数秒内交付——每一项都可端到端追溯至数据源。" },
      ],
    },
    arch: {
      tag: "系统架构",
      h2: "一个知识图谱,一道数据盲边界。",
      sub: "本地引擎在安全边界内将实时机器遥测与工厂的运营知识相连接。原始数据留在内部——只有抽象化的上下文在流动。",
      tour: [
        { label: "第1步 — 数据流入", text: "七路工厂智能流——历史数据库、控制系统、MES、维护、实验室、文档、ERP——以原始遥测形式流入。" },
        { label: "第2步 — 数据盲边界", text: "在边界处,原始负载被抽象为运营元数据。任何原始数据都不会越界。" },
        { label: "第3步 — 知识图谱", text: "机器遥测与人类运营知识(SOP、班次日志、工厂规则)融合——完全在本地计算。" },
        { label: "第4步 — 专业智能体", text: "智能体用操作语言理解您的任务,只检索所需的上下文。" },
        { label: "第5步 — 可信输出", text: "仪表板、根本原因、报警洞察和建议回流——每一项都可追溯至源头。" },
        { label: "第6步 — 云被拒之门外", text: "通往前沿云AI的路径被架构阻断,而非仅靠策略。您的遥测永不离开工厂。" },
      ],
      legend: ["原始遥测 — 留在工厂内", "抽象化元数据 — 智能体的推理基础", "云端外传 — 已阻断"],
      defaultDesc: "将鼠标悬停在数据源、边界或知识图谱上,追踪数据如何流动——以及什么永远不会离开。",
      replay: "重播导览",
      connectors: "连接器",
      yourData: "您的数据",
      runsLocally: "本地运行",
      blocked: "已阻断",
      kgMobileTitle: "知识图谱",
      kgMobileBody: "数据盲边界之后的双流上下文:实时遥测与SOP、班次日志和工厂规则融合。原始数据永不离开。",
      agentsTitle: "专业智能体",
      outputsTitle: "可信输出",
      descs: {
        hub: "双流上下文:实时机器遥测与工厂的运营知识融合——完全在本地计算。",
        boundary: "原始负载在边界处被抽象为运营元数据。模型在本地运行——没有任何数据被外传到云端。",
        agents: "专业智能体理解操作意图,只检索相关上下文,并组装带有完整来源追溯的答案。",
        cloud: "通用云AI需要将原始遥测传出工厂。边界将其阻断——靠架构,而非策略。",
      },
      sourceDescs: [
        "时间序列工艺趋势以原始遥测形式流入——只有抽象化的运营元数据越过边界。",
        "实时控制信号和报警留在您的控制网络内。智能体基于上下文推理,绝不接触原始负载。",
        "生产订单、OEE事件和车间执行数据从MES层流入。",
        "工单和维护历史将每次根因分析锚定在现场实际完成的工作上。",
        "实验室结果与批次上下文融合,使质量偏差与工艺条件直接关联。",
        "操作手册和工厂规则约束每一项建议——绝不超出您预先验证的参数。",
        "订单、计划和库存将生产上下文与业务优先级连接起来。",
      ],
      outputDescs: [
        "按请求从工厂数据构建的实时运营仪表板。",
        "偏差和停机被追溯到具体原因,并附证据。",
        "报警的含义、触发原因以及后续措施。",
        "与已验证运行参数绑定的可执行后续步骤。",
      ],
    },
    deliver: {
      tag: "您将获得",
      h2: "答案,而非原始文本。",
      liveExample: "实时示例——“为冲压车间构建OEE仪表板。”",
      cards: [
        { title: "仪表板", body: "按请求构建,由实时工厂数据驱动——OEE、停机、质量、能耗。" },
        { title: "根本原因分析", body: "偏差或停机背后的具体原因——识别并附证据。" },
        { title: "报警洞察", body: "报警的含义、触发原因以及应对措施。" },
        { title: "建议", body: "可执行的后续步骤——每一项都与预先验证的运行参数绑定。" },
      ],
    },
    bench: {
      tag: "基准测试",
      h2: "超越前沿AI。在您的数据上。",
      sub: "相同任务、相同工厂数据集、并排对比——由独立工艺工程师评分。聚焦检索在每项指标上都胜过原始文本处理。",
      stats: [
        { label: "准确率", sub: "前沿模型为15.8–17.7%" },
        { label: "每任务成本", sub: "前沿模型高达约$1.80/任务" },
        { label: "每任务Token", sub: "前沿模型为500k+" },
      ],
      theirs: "前沿AI模型",
      bars: ["准确率", "每任务成本", "每任务Token"],
      footnote: "基准测试:2026年8月 · 相同工厂数据集 · 人工验证评分。成本和Token柱越低越好。",
    },
    security: {
      tag: "安全与部署",
      h2: "您的遥测永不离开工厂。",
      sub: "Streamline360以架构而非策略实现隐私优先。数据盲边界将专有配方和工艺数据保留在您的安全边界内。",
      cards: [
        { title: "本地与边缘部署", body: "直接安装在边缘或工厂网络内。无云依赖,无延迟障碍。" },
        { title: "本地数据边界", body: "原始遥测被抽象为运营元数据。模型在本地运行——没有任何外传。" },
        { title: "零幻觉", body: "建议严格受限于您的工厂操作手册和预先验证的参数。" },
        { title: "完全可审计", body: "每个洞察都链接到具体的传感器标签、时间戳和批准的程序。" },
      ],
      deployLabel: "部署",
      deployCaption: "在您的边界内。由您掌控。",
      compliance: "合规路线图",
    },
    team: {
      tag: "领导团队",
      h2: "由了解车间的人打造。",
      sub: "一支横跨工业AI、企业软件和制造运营的创始团队——执着于为关键基础设施提供安全的智能。",
      members: [
        { role: "联合创始人 & CEO", bio: "企业软件与工业市场拓展。负责战略、合作伙伴关系和OEM渠道网络。" },
        { role: "联合创始人 & CTO", bio: "工业AI与边缘系统。知识图谱引擎和数据盲边界的架构师。" },
        { role: "产品负责人", bio: "制造运营与HMI设计。负责车间自然语言智能体体验。" },
      ],
      onRequest: "简历可应要求提供",
    },
    contact: {
      tag: "开始使用",
      h2: "在您的数据上见证效果。",
      sub: "预约技术演示——我们将在您的工厂数据样本上运行Streamline360,本地部署,在您的边界之内。",
      bullets: ["60天技术验证路径", "部署于边缘或本地", "开箱即连历史数据库、SCADA和CMMS"],
      name: "姓名",
      company: "公司",
      email: "工作邮箱",
      message: "您希望Streamline360分析什么?(可选)",
      submit: "申请演示",
      toastOk: "已收到请求。我们的团队将尽快与您联系。",
      toastErr: "出现错误,请重试。",
      note: "您的信息由我们保管。演示期间不会有数据离开您的工厂。",
    },
    footer: {
      tagline: "面向企业制造的私有、本地部署智能体AI。",
      impressum: "公司信息",
      madeIn: "面向企业制造的私有工业AI——哥本哈根制造。",
      rights: "© 2026 Streamline360 ApS. 版权所有。",
      neverLeaves: "您的数据永不离开工厂。",
      privacy: "隐私政策",
      terms: "使用条款",
      contact: "联系我们",
    },
  },
  ko: {
    nav: { platform: "플랫폼", architecture: "아키텍처", benchmark: "벤치마크", security: "보안", team: "팀" },
    bookDemo: "데모 예약",
    hero: {
      eyebrow: "엔터프라이즈 제조를 위한 프라이빗 산업 AI",
      line1: "작업을 지시하세요.",
      line2: "에이전트가 실행합니다.",
      line3: "모든 인사이트 추적 가능.",
      sub: "Streamline360은 엔터프라이즈 제조를 위해 구축된 에이전틱 AI 플랫폼입니다. 일상적인 운영 언어로 물어보기만 하면, 전문 에이전트가 지식 그래프에서 실시간 플랜트 컨텍스트를 가져와 몇 초 만에 답합니다. 온프레미스와 엣지에서 실행되며, 데이터는 공장 밖으로 나가지 않습니다.",
      ctaBenchmark: "벤치마크 보기",
      stats: [
        { value: "90.2%", label: "벤치마크 정확도" },
        { value: "~$0.05", label: "작업당 비용" },
        { value: "수 초", label: "인사이트까지의 시간" },
        { value: "100%", label: "소스까지 추적 가능" },
      ],
    },
    marquee: ["환각 제로", "100% 추적 가능", "지식 그래프 기반", "온프레미스 & 엣지", "자연어 쿼리", "OPC-UA · MQTT · 히스토리안 API"],
    problem: {
      tag: "문제",
      h2: "귀사의 공장은 아직 언어 모델의 힘을 사용할 수 없습니다.",
      sub: "글로벌 제조업은 계획되지 않은 다운타임으로 수십억을 잃고 있습니다. 그러나 범용 AI는 세 가지 구조적 이유로 공장 현장에 들어오지 못합니다.",
      cards: [
        { title: "데이터 주권의 벽", body: "보안 및 핵심 인프라 규정은 독점 텔레메트리와 공정 레시피를 공장 경계 밖으로 보내는 것을 엄격히 금지합니다. 클라우드 AI는 선택지가 아닙니다." },
        { title: "레거시 데이터 사일로", body: "중요한 공장 인텔리전스는 분산된 히스토리안, 레거시 SCADA 시스템, 제어 네트워크, 종이 교대 일지에 갇혀 있습니다." },
        { title: "파일럿의 함정", body: "범용 프론티어 모델은 너무 느리고, 지속적인 쿼리에는 너무 비싸며, 중요한 생산 이벤트에서 환각을 일으키기 쉽습니다." },
      ],
    },
    stepper: {
      tag: "작업에서 결과까지",
      h2: "작업을 지시하면, 나머지는 에이전트가 처리합니다.",
      steps: [
        { n: "01", label: "작업 지시", quote: "“어제 3번 라인이 왜 멈췄지?”", body: "공장 직원은 표준 운영 언어로 질문하기만 하면 됩니다. 쿼리 작성도, 대시보드 설정도, 데이터보내기도 필요 없습니다." },
        { n: "02", label: "에이전트가 컨텍스트 수집", body: "전문 에이전트가 지식 그래프에서 플랜트 데이터와 관계를 가져옵니다 — 작업에 필요한 것만." },
        { n: "03", label: "에이전트가 분석", body: "다운타임, 알람, 파라미터, 트렌드를 검증된 플랜트 컨텍스트와 대조해 분석합니다 — 원시 텍스트 추측은 하지 않습니다." },
        { n: "04", label: "에이전트가 결과 생성", body: "대시보드가 구축되고, 근본 원인이 식별되며, 권장 사항이 만들어집니다 — 모두 근거와 함께." },
        { n: "05", label: "결과 수신", body: "인사이트, 권장 사항, 근본 원인이 몇 초 만에 전달됩니다 — 모두 소스까지 엔드투엔드로 추적 가능합니다." },
      ],
    },
    arch: {
      tag: "아키텍처",
      h2: "하나의 지식 그래프. 데이터 블라인드 경계.",
      sub: "로컬 엔진이 실시간 기계 텔레메트리를 안전한 경계 내에서 공장의 운영 지식과 연결합니다. 원시 데이터는 내부에 머물고, 추상화된 컨텍스트만 이동합니다.",
      tour: [
        { label: "1단계 — 데이터 유입", text: "일곱 갈래의 공장 인텔리전스(히스토리안, 제어 시스템, MES, 보전, 실험실, 문서, ERP)가 원시 텔레메트리로 유입됩니다." },
        { label: "2단계 — 데이터 블라인드 경계", text: "경계에서 원시 페이로드는 운영 메타데이터로 추상화됩니다. 원시 데이터는 절대 경계를 넘지 않습니다." },
        { label: "3단계 — 지식 그래프", text: "기계 텔레메트리가 인간의 운영 지식(SOP, 교대 일지, 공장 규칙)과 융합됩니다 — 완전히 온프레미스에서 계산됩니다." },
        { label: "4단계 — 전문 에이전트", text: "에이전트가 운영 언어로 작업을 해석하고 필요한 컨텍스트만 가져옵니다." },
        { label: "5단계 — 신뢰할 수 있는 출력", text: "대시보드, 근본 원인, 알람 인사이트, 권장 사항이 되돌아옵니다 — 모두 소스까지 추적 가능합니다." },
        { label: "6단계 — 클라우드는 밖에", text: "프론티어 클라우드 AI로의 경로는 정책이 아닌 아키텍처로 차단됩니다. 텔레메트리는 공장 밖으로 나가지 않습니다." },
      ],
      legend: ["원시 텔레메트리 — 공장 내 유지", "추상화된 메타데이터 — 에이전트의 추론 대상", "클라우드 유출 — 차단됨"],
      defaultDesc: "데이터 소스, 경계, 지식 그래프 위에 마우스를 올려 데이터 흐름과 절대 나가지 않는 것을 확인하세요.",
      replay: "투어 다시 보기",
      connectors: "커넥터",
      yourData: "고객 데이터",
      runsLocally: "로컬 실행",
      blocked: "차단됨",
      kgMobileTitle: "지식 그래프",
      kgMobileBody: "데이터 블라인드 경계 뒤의 듀얼 스트림 컨텍스트: 실시간 텔레메트리와 SOP, 교대 일지, 공장 규칙이 융합됩니다. 원시 데이터는 절대 나가지 않습니다.",
      agentsTitle: "전문 에이전트",
      outputsTitle: "신뢰할 수 있는 출력",
      descs: {
        hub: "듀얼 스트림 컨텍스트: 실시간 기계 텔레메트리와 공장의 운영 지식이 융합 — 완전히 온프레미스에서 계산됩니다.",
        boundary: "원시 페이로드는 경계에서 운영 메타데이터로 추상화됩니다. 모델은 로컬에서 실행되며 클라우드로 유출되지 않습니다.",
        agents: "전문 에이전트가 운영 의도를 해석하고 관련 컨텍스트만 가져와 완전한 소스 추적이 포함된 답변을 구성합니다.",
        cloud: "범용 클라우드 AI는 원시 텔레메트리의 외부 전송이 필요합니다. 경계가 이를 차단합니다 — 정책이 아닌 아키텍처로.",
      },
      sourceDescs: [
        "시계열 공정 트렌드가 원시 텔레메트리로 유입되고, 추상화된 운영 메타데이터만 경계를 넘습니다.",
        "실시간 제어 신호와 알람은 제어 네트워크에 유지됩니다. 에이전트는 원시 페이로드가 아닌 컨텍스트로 추론합니다.",
        "생산 오더, OEE 이벤트, 현장 실행 데이터가 MES 계층에서 유입됩니다.",
        "작업 지시서와 보전 이력이 모든 근본 원인 분석을 현장에서 실제로 수행된 작업에 고정시킵니다.",
        "실험실 결과가 배치 컨텍스트와 융합되어 품질 편차가 공정 조건과 직접 상관됩니다.",
        "운영 매뉴얼과 공장 규칙이 모든 권장 사항을 제한합니다 — 사전 검증된 파라미터를 넘지 않습니다.",
        "주문, 일정, 재고가 생산 컨텍스트를 비즈니스 우선순위와 연결합니다.",
      ],
      outputDescs: [
        "플랜트 데이터로 요청 시 구축되는 실시간 운영 대시보드.",
        "편차와 정지를 구체적인 원인까지 근거와 함께 추적.",
        "알람의 의미, 발생 이유, 다음 조치.",
        "검증된 운영 파라미터에 연결된 실행 가능한 다음 단계.",
      ],
    },
    deliver: {
      tag: "제공되는 것",
      h2: "원시 텍스트가 아닌, 답을.",
      liveExample: "실시간 예시 — “프레스 공장용 OEE 대시보드를 만들어줘.”",
      cards: [
        { title: "대시보드", body: "요청 시 구축되며 실시간 플랜트 데이터로 구동 — OEE, 다운타임, 품질, 에너지." },
        { title: "근본 원인 분석", body: "편차나 정지의 구체적인 원인을 식별하고 근거를 제시합니다." },
        { title: "알람 인사이트", body: "알람의 의미, 발생 이유, 대응 방법." },
        { title: "권장 사항", body: "실행 가능한 다음 단계 — 모두 사전 검증된 운영 파라미터에 연결됩니다." },
      ],
    },
    bench: {
      tag: "벤치마크",
      h2: "프론티어 AI를 능가. 고객의 데이터로.",
      sub: "동일한 작업, 동일한 공장 데이터셋을 나란히 놓고 독립적인 공정 엔지니어가 채점했습니다. 집중 검색이 모든 지표에서 원시 텍스트 처리를 앞섰습니다.",
      stats: [
        { label: "정확도", sub: "프론티어 모델은 15.8–17.7%" },
        { label: "작업당 비용", sub: "최대 ~$1.80/작업과 비교" },
        { label: "작업당 토큰", sub: "프론티어 모델은 500k+" },
      ],
      theirs: "프론티어 AI 모델",
      bars: ["정확도", "작업당 비용", "작업당 토큰"],
      footnote: "벤치마크: 2026년 8월 · 동일 공장 데이터셋 · 인간 검증 채점. 비용과 토큰 막대는 낮을수록 좋습니다.",
    },
    security: {
      tag: "보안 및 배포",
      h2: "텔레메트리는 공장 밖으로 나가지 않습니다.",
      sub: "Streamline360은 정책이 아닌 아키텍처로 프라이버시 우선입니다. 데이터 블라인드 경계가 독점 레시피와 공정 데이터를 안전한 경계 안에 유지합니다.",
      cards: [
        { title: "온프레미스 & 엣지", body: "엣지 또는 공장 네트워크에 직접 설치됩니다. 클라우드 의존도도, 지연 장벽도 없습니다." },
        { title: "로컬 데이터 경계", body: "원시 텔레메트리는 운영 메타데이터로 추상화됩니다. 모델은 로컬에서 실행되며 유출되지 않습니다." },
        { title: "환각 제로", body: "권장 사항은 시설 운영 매뉴얼과 사전 검증된 파라미터에 엄격히 제한됩니다." },
        { title: "완전한 감사 가능성", body: "모든 인사이트가 특정 센서 태그, 타임스탬프, 승인된 절차와 연결됩니다." },
      ],
      deployLabel: "배포",
      deployCaption: "고객의 경계 안에서. 고객의 통제 하에.",
      compliance: "컴플라이언스 로드맵",
    },
    team: {
      tag: "리더십",
      h2: "현장을 아는 사람들이 만듭니다.",
      sub: "산업 AI, 엔터프라이즈 소프트웨어, 제조 운영을 아우르는 창업 팀 — 핵심 인프라에 안전한 인텔리전스를 제공하는 데 집착합니다.",
      members: [
        { role: "공동 창업자 & CEO", bio: "엔터프라이즈 소프트웨어와 산업 GTM. 전략, 파트너십, OEM 채널 네트워크를 총괄합니다." },
        { role: "공동 창업자 & CTO", bio: "산업 AI와 엣지 시스템. 지식 그래프 엔진과 데이터 블라인드 경계의 설계자입니다." },
        { role: "제품 책임자", bio: "제조 운영과 HMI 디자인. 플랜트 현장의 자연어 에이전트 경험을 담당합니다." },
      ],
      onRequest: "프로필은 요청 시 제공",
    },
    contact: {
      tag: "시작하기",
      h2: "고객의 데이터로 확인하세요.",
      sub: "기술 데모를 예약하세요 — 고객의 플랜트 데이터 샘플에 대해 온프레미스, 고객의 경계 안에서 Streamline360을 실행합니다.",
      bullets: ["60일 기술 검증 경로", "엣지 또는 온프레미스에 배포", "히스토리안, SCADA, CMMS에 기본 연결"],
      name: "성명",
      company: "회사",
      email: "업무용 이메일",
      message: "Streamline360으로 무엇을 분석하고 싶으세요? (선택)",
      submit: "데모 요청",
      toastOk: "요청이 접수되었습니다. 담당 팀이 곧 연락드리겠습니다.",
      toastErr: "문제가 발생했습니다. 다시 시도해 주세요.",
      note: "고객 정보는 당사에서 보관합니다. 데모 중에도 데이터가 공장 밖으로 나가지 않습니다.",
    },
    footer: {
      tagline: "엔터프라이즈 제조를 위한 프라이빗 온프레미스 에이전틱 AI.",
      impressum: "회사 정보",
      madeIn: "엔터프라이즈 제조용 프라이빗 산업 AI — 코펜하겐에서 제작.",
      rights: "© 2026 Streamline360 ApS. All rights reserved.",
      neverLeaves: "데이터는 공장 밖으로 나가지 않습니다.",
      privacy: "개인정보 처리방침",
      terms: "이용 약관",
      contact: "문의",
    },
  },
};
